import * as K from "../../lib/const.js";
import * as api from "../../lib/api.js";
import * as audio from "../../lib/audio.js";
import * as chirp from "../../lib/chirp.js";
import * as claims from "../../lib/claims.js";
import * as sign from "../../lib/sign.js";
import * as store from "../../lib/store.js";
import * as telemetry from "../../lib/telemetry.js";
import { assertFiniteConfidences, claimDeltaRefusalReason } from "./claim-guard";
import {
  CLAIM_DELTA_SPLIT_SEC,
  ClaimDeltaOutbox,
  draftBytes,
  sliceGapsForDraft,
  type ClaimDeltaDraft,
  type ClaimDeltaContext,
} from "./claim-delta-outbox";
import { Latch } from "./latch";
import {
  FinalWatermarkGate,
  MarkerTerminalError,
  runWithDeadline,
} from "./claim-delta-final-watermark";
import {
  FINALISE_RETRY_EXHAUSTED_MESSAGE,
  FINALISE_RETRY_TIMER,
  FINALISE_RETRYING_MESSAGE,
  FinaliseRetryAbortedError,
  FinaliseRetryController,
  FinaliseRetryExhaustedError,
  type FinaliseRetryTimer,
} from "./finalise-errors";
import { WRAP_STEPS, nextWrapAt } from "./wrapup";
export { WRAP_STEPS, wrapIndex } from "./wrapup";
import { sayOn } from "../routes/use-screen-status";
import type { SessionOut, SessionResultPendingOut } from "../contract/api";
import { t } from "../contract/strings";
import {
  processingStageOf,
  recordingIdentityBlock,
  summaryPendingOf,
  useApp,
  type SessionHandle,
  type SessionPhase,
} from "../state/store";
import { capabilitiesChanged, markCapabilitiesReported } from "./capabilities";
import { durationWords } from "./duration-words";
import {
  RESULT_POLL_ATTEMPTS,
  RESULT_POLL_EARLY_MS,
  RESULT_POLL_FAST_MS,
  RESULT_POLL_MS,
} from "../result-poll";
import { vadDiagnosticDue } from "./vad-diagnostics";
import { streamProbeManualMode } from "./audio-uplink";
import { createSessionEventTelemetry } from "./session-event-telemetry";
import { RECORDING_PAUSED_NOTICE, recordingPaused } from "./server-config";
import { captureAnalytics } from "../analytics";
import {
  awaitAudioDelivered,
  awaitStreamClosed,
  resetStreamForNewRecording,
  setStreamSessionStateHandler,
  settleStreamReadiness,
  streamReadiness,
  startStreamIfEnabled,
  stopStreamForRecordingEnd,
} from "./stream-session";

/**
 * The live session: recorder, scoring, the claim outbox, wrap-up.
 *
 * **Everything that decides anything lives in this module, not in React.** The 500 ms tick,
 * the ~20 s chunk cadence, the outbox and the scoring flag are plain module state, read and
 * written directly. That is not a stylistic preference: `scoring` and `chunkCursor` are read
 * across `await`s, and a value reached through a React selector is a snapshot that may
 * already be stale by the time the continuation runs -- which here means either scoring a
 * span twice or skipping one. What the store gets is *readings*: elapsed, claimed, windows,
 * which wrap-up stage is current. The level meter is not even that; it reads `takePeak`
 * through `peak()` on the component's own animation frame.
 *
 * Navigation is React's. The engine moves `phase` and the screen reacts, because an engine
 * that could navigate would be an engine that has to know about routes.
 */

const SESSION_PATH = "/session";

/** How often a recording device asks whether the session is still running, while the SSE
    event stream is down. Three seconds because "everyone stop now" wants to feel immediate,
    and a claim-delta ack -- which carries the same answer for free -- only goes out on the
    chunk cadence. */
const STATE_POLL_SEC = 3;

/** The same poll, while the SSE stream is healthy (#440). The stream then carries state
    changes and the roster, so the full poll is a heartbeat, not the discovery path. */
const STATE_POLL_SSE_SEC = 60;

/**
 * The state poll's interval, by SSE health (#440).
 *
 * With the event stream connected, `closing` arrives as an SSE `state_change`, on every
 * claim-delta ack, and on the uplink's advisory frame -- the 3 s poll is paying full price
 * for an answer three other channels already carry. The heartbeat that remains still
 * refreshes the roster and backstops a stream that silently wedged, so the poll is demoted,
 * not removed.
 */
export function statePollEverySec(sseConnected: boolean): number {
  return sseConnected ? STATE_POLL_SSE_SEC : STATE_POLL_SEC;
}

/** Sparse capture diagnostics: enough to catch a dead iOS worklet without a per-tick trace. */
const CAPTURE_HEALTH_INTERVAL_MS = 5_000;

/* The result-poll cadence itself lives in `../result-poll`, shared with the
 * result screen's cold-load loop so the two waits cannot drift apart (#435). */

/**
 * The session states in which nobody has ended the conversation yet (#286).
 *
 * The server's own `OPEN_STATES` (`routers/sessions.py`), mirrored here because it is
 * what the 202's `session_state` has to be compared against. A `Set` and not a regex or
 * a `startsWith`, so an unknown state from a newer server falls through to the merge
 * path rather than being guessed at.
 */
const OPEN_STATES: ReadonlySet<string> = new Set(["open", "recording"]);

/**
 * How long this device will wait for somebody else to end the session.
 *
 * Twenty minutes, comfortably above the 900 s stream cap, because the thing being
 * waited for is a person: the creator's device is still recording, and there is nothing
 * wrong for this one to report until that stops. It is not the result budget --
 * spending the `result-poll` module's total budget of patience on a session that has not started
 * merging would leave nothing for the merge itself, which is the wait the budget was
 * measured for.
 *
 * The session's own `expires_at` is a *ceiling* on this, not an alternative to it (Q3):
 * whichever comes first wins. Twenty minutes is a UX bound on "waiting for someone
 * else"; `expires_at` is the session's lifetime and must never be waited past. An
 * absent `expires_at` -- an older server -- leaves this bound standing on its own.
 */
const WAIT_FOR_END_MAX_MS = 20 * 60 * 1000;

/**
 * The 202's shape, as much of it as this device reads: a partial view of the generated
 * `SessionResultPendingOut`. Every accessor below still type-guards what it reads, so an
 * absent field resolves to *unknown*, never to a state (#286).
 */
type PendingResultBody = Partial<SessionResultPendingOut>;

/**
 * The server's `roster` (`protocol.Participant` pairs) as participant id to name.
 *
 * Entries without a usable id or name are dropped rather than guessed at, so a
 * malformed entry cannot put a blank line on the recording screen.
 */
export function rosterFrom(entries: unknown): Record<string, string> {
  if (!Array.isArray(entries)) return {};
  const roster: Record<string, string> = {};
  for (const entry of entries) {
    if (!entry || typeof entry !== "object") continue;
    const { participant_id: id, display_name: name } = entry as Record<string, unknown>;
    if (typeof id !== "string" || !id) continue;
    if (typeof name !== "string" || !name.trim()) continue;
    roster[id] = name;
  }
  return roster;
}

/**
 * The session state this poll reports, or `null` when the response does not say.
 *
 * A typed accessor rather than `out.session_state as string`, and the whole of #286's
 * defect class in four lines: absence must be *missing*, never a state and never a
 * falsy one. An older server omits the field, and a client that read `!state` as "not
 * open" would show the merge step through the exact window this change exists to name.
 * No numeric coercion anywhere near it -- the same rule the streaming edge gate applies
 * to recorded values, for the same reason: coercing an absent value yields a number
 * that looks like an answer.
 */
function pendingSessionState(out: PendingResultBody | undefined): string | null {
  const value = out?.session_state;
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** The display names still recording, or `[]` when the response does not say. */
function pendingNames(out: PendingResultBody | undefined): string[] {
  const value = out?.pending;
  if (!Array.isArray(value)) return [];
  return value.filter((name): name is string => typeof name === "string" && name.length > 0);
}

/**
 * The session's expiry as epoch milliseconds, or `null` when it is absent or unparseable.
 *
 * `null` and not `0`: a zero would be in the past, and a wait bounded by a value in the
 * past ends instantly -- which is how "this field is missing" would have become "this
 * session has expired" for every device talking to an older server.
 */
function pendingExpiresAtMs(out: PendingResultBody | undefined): number | null {
  const value = out?.expires_at;
  if (typeof value !== "string" || value.length === 0) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

interface Engine {
  recorder: InstanceType<typeof audio.Recorder> | null;
  timer: ReturnType<typeof setInterval> | null;
  session: SessionHandle | null;
  chunkCursor: number;
  lastStateCheck: number;
  windows: number;
  claimedSec: number;
  /** Held while Stop or Discard is working. See `Latch` for the two presses it guards. */
  stopping: Latch;
  reported: boolean;
  isCreator: boolean;
  /** Claim deltas the server refused outright this session. Speech nobody is credited for. */
  claimsRefused: number;
  /**
   * Scores windows as their audio arrives, so a stop press owes one window and not a chunk.
   * Created per session because it caches this recording's scores. See `createRollingScorer`.
   */
  scorer: ReturnType<typeof claims.createRollingScorer> | null;
  /**
   * The continuous claim-delta writer (#170-A), or `null` before admission.
   *
   * Present only when the deployment reports `claim_deltas_enabled` *and* this device
   * held an accepted producer lease before its first delta. Both are decided once per
   * session: a device does not change payload protocol mid-conversation.
   */
  deltaOutbox: ClaimDeltaOutbox | null;
  /** Owns activation and the final watermark for this recording (#170-B). */
  deltaGate: FinalWatermarkGate | null;
  /**
   * Which session the installed claim-delta writer belongs to (#170-B).
   *
   * The outbox and the gate close over their session id, so before this it was only
   * knowable by watching which URL they posted to -- which meant "did a superseded
   * decision install its writer over a live recording?" could only be answered
   * indirectly, through whatever traffic happened to occur. Recording it makes the
   * writer's identity a fact rather than an inference.
   */
  deltaSessionId: string | null;
  /**
   * Bumped once per `startRecording` (#170-B).
   *
   * `engine` is module state shared by every recording, and the delta-mode decision is an
   * async chain with network calls in it. Without a generation, a decision started for
   * recording N can resolve *after* recording N+1 has begun and install N's outbox, gate
   * and epoch over N+1's -- a writer signing for one session against another's ledger.
   * Every step that mutates `engine` re-checks this first.
   */
  recordingGeneration: number;
  /** Whether the delta path has been chosen for this session. Decided once. */
  deltaMode: boolean;
  /** Whether that decision has been taken yet, which is not the same as its answer. */
  deltaDecided: boolean;
  /** Single-flight finalise/leave retry, created only when the report is first needed. */
  finaliseRetry: FinaliseRetryController<FinaliseResponse> | null;
  /** One epoch per recording. Delta sequences are scoped to it. */
  timelineEpoch: string;
  /** Monotonic within the epoch, and the source of stable delta and claim ids. */
  deltaCounter: number;
  /**
   * Split pieces of one over-long harvest that the bounded queue could not take yet.
   *
   * Held here rather than dropped, and harvesting is blocked while it is non-empty --
   * which is what keeps it bounded and what makes "no signed claim is ever discarded"
   * true of the whole path rather than only of the queue.
   */
  deltaCarry: ClaimDeltaDraft[];
  /**
   * The delta path has stopped for good this session.
   *
   * Terminal rather than lossy, and that is the policy: nothing already scored is
   * discarded, but neither is more of it accumulated behind a writer that can never
   * send it. Recording and audio upload continue.
   */
  deltaHalted: boolean;
  /** Which pause was already reported, so it is said once per episode and not per tick. */
  deltaHoldReported: "" | "full" | "closed";
  /** Scored pieces a terminal stop released rather than sent. Never silent. */
  deltaAbandoned: number;
  deltaAbandonedBytes: number;
  chirping: boolean;
  chirpCtx: AudioContext | null;
  wakeLock: WakeLockSentinel | null;
  /** Retry timer for rebuilding capture after the context died (C7), or null. */
  captureRebuildTimer: ReturnType<typeof setTimeout> | null;
  /** Highest wrap-up stage reached. Monotonic -- a later stage never un-does an earlier. */
  wrapAt: number;
  eventAbort: AbortController | null;
  /** Whether the SSE event stream is currently connected; demotes the state poll (#440). */
  sseConnected: boolean;
  /** Last metadata-only capture heartbeat, measured with performance.now(). */
  lastCaptureHealthPerf: number;
  /** Dedupe repeated copies of one client-side failure. */
  lastCaptureErrorKey: string;
  lastCaptureErrorPerf: number;
  recordingStartedAtMs: number | null;
  /** Fired by `cancelSetup`: Start or Join is abandoned before the first audio (#658). */
  setupAbort: AbortController | null;
}

const engine: Engine = {
  recorder: null,
  timer: null,
  session: null,
  chunkCursor: 0,
  lastStateCheck: 0,
  windows: 0,
  claimedSec: 0,
  stopping: new Latch(),
  reported: false,
  isCreator: false,
  claimsRefused: 0,
  scorer: null,
  deltaOutbox: null,
  deltaGate: null,
  deltaSessionId: null,
  recordingGeneration: 0,
  deltaMode: false,
  deltaDecided: false,
  finaliseRetry: null,
  timelineEpoch: "",
  deltaCounter: 0,
  deltaCarry: [],
  deltaHalted: false,
  deltaHoldReported: "",
  deltaAbandoned: 0,
  deltaAbandonedBytes: 0,
  chirping: false,
  chirpCtx: null,
  wakeLock: null,
  captureRebuildTimer: null,
  wrapAt: -1,
  eventAbort: null,
  sseConnected: false,
  lastCaptureHealthPerf: -Infinity,
  lastCaptureErrorKey: "",
  lastCaptureErrorPerf: -Infinity,
  recordingStartedAtMs: null,
  setupAbort: null,
};

/**
 * A scoring pass is running. Compute only -- never delivery.
 *
 * [V] Delivery used to be inside this flag, so a slow uplink stopped a device claiming
 * altogether; see `outbox.ts` for the session that cost. The flag now covers what its name
 * always implied, and `chunkCursor` advances when a span is *scored*, because a span already
 * scored is not worth scoring twice.
 */
let scoring = false;

/** Injectable only for deterministic retry tests; production uses the browser timer port. */
let finaliseRetryTimerForTest: FinaliseRetryTimer | null = null;

export function setFinaliseRetryTimerForTest(timer: FinaliseRetryTimer | null): void {
  finaliseRetryTimerForTest = timer;
}

/**
 * The clock the "waiting for the others" bound is measured on (#286).
 *
 * A port for the same reason the finalise retry has one: {@link WAIT_FOR_END_MAX_MS} is
 * twenty minutes, and a test that proved the bound by waiting it out would not be run.
 * Only the waiting branch of `pollResult` reads it -- the result budget keeps
 * `performance.now()`, so this seam cannot quietly reshape the poll cadence the stop
 * path is measured with.
 *
 * `epochMs` is separate from `now` because the two answer different questions: `now` is
 * a monotonic span ("how long have I been waiting"), `epochMs` is compared against the
 * server's `expires_at` ("has the session's own lifetime run out").
 */
export interface WaitForEndClock {
  now(): number;
  epochMs(): number;
  sleep(ms: number): Promise<void>;
}

const WAIT_FOR_END_CLOCK: WaitForEndClock = {
  now: () => performance.now(),
  epochMs: () => Date.now(),
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
};

let waitForEndClockForTest: WaitForEndClock | null = null;

export function setWaitForEndClockForTest(clock: WaitForEndClock | null): void {
  waitForEndClockForTest = clock;
}

function waitClock(): WaitForEndClock {
  return waitForEndClockForTest ?? WAIT_FOR_END_CLOCK;
}

const say = (message: string, kind: "" | "ok" | "warn" | "err" = "") =>
  sayOn(SESSION_PATH, message, kind);

/** Explain who is still recording so the app can say why the session remains open. */
function sessionStillRecording(names: string[]): string {
  if (names.length === 1) return t("session.still_in_one", { name: names[0] });
  if (names.length > 1) return t("session.still_in_many");
  // No names is a real case -- an older server's 202, or a roster this device never
  // saw -- and it must still produce a sentence rather than an empty gap.
  return t("session.still_in_another");
}

/**
 * The waiting status line, which must not talk over a warning (#286, S2.c).
 *
 * By the time this device is waiting for other people, the wrap-up may already have
 * said something that matters more and is not going to stop being true: claim deltas
 * refused, a last stretch that could not be scored. Both are `warn`, both are
 * permanent holes in this device's contribution, and overwriting either with "Waiting
 * for Ravi to end the session…" every 1500 ms would bury it. The wrap card's own
 * `waiting` step carries the state in that case; the status line keeps the loss.
 */
function sayWaitingFor(names: string[]): void {
  const kind = useApp.getState().screenStatus?.kind;
  if (kind === "warn" || kind === "err") return;
  const who = names.join(", ");
  say(who ? t("session.waiting_for", { who }) : t("session.waiting_for_others"));
}

const patch = (p: Parameters<ReturnType<typeof useApp.getState>["setSessionState"]>[0]) =>
  useApp.getState().setSessionState(p);

/** The level meter's source. Reset-on-read, so there is exactly one reader. */
export function peak(): number {
  return engine.recorder?.takePeak() ?? 0;
}

/**
 * Read-only adapter seam for the audio uplink.
 *
 * The uplink may observe the recorder already owned by this engine, but it never starts or
 * stops it. Capture lifecycle and timing therefore stay entirely with the engine.
 */
export function activeRecorder(): InstanceType<typeof audio.Recorder> | null {
  return engine.recorder;
}

/** The monotonic performance.now() anchor when recording began, or null if not recording. */
export function activeRecordingStartedAt(): number | null {
  return engine.recordingStartedAtMs;
}

function captureHealthDetail(): Record<string, string | number | boolean | null> {
  const recorder = engine.recorder;
  const app = useApp.getState();
  const health = recorder?.captureHealth();
  const acceptsAudio = app.serverConfig?.accepts_audio;
  return {
    audio_context_state: health?.audio_context_state ?? null,
    track_ready_state: health?.track_ready_state ?? null,
    track_enabled: health?.track_enabled ?? null,
    sample_count: health?.sample_count ?? 0,
    captured_sec: health?.captured_sec ?? 0,
    worklet_messages: health?.worklet_messages ?? 0,
    sample_rate: health?.sample_rate ?? null,
    timeline_rate: health?.timeline_rate ?? null,
    audio_uploads_enabled: typeof acceptsAudio === "boolean" ? acceptsAudio : null,
    scoring_ready: Boolean(app.template && app.credentials && app.keypair),
    elapsed_sec: recorder?.seconds ?? 0,
    chunk_cursor_sec: engine.chunkCursor,
    windows: engine.windows,
    scoring_active: scoring,
  };
}

function errorMessage(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  return (message || "unknown error").slice(0, 64);
}

function logCaptureHealth(reason: string, force = false): void {
  const now = performance.now();
  if (!force && now - engine.lastCaptureHealthPerf < CAPTURE_HEALTH_INTERVAL_MS) return;
  engine.lastCaptureHealthPerf = now;
  telemetry.log("capture_health", { reason, ...captureHealthDetail() });
}

function logCaptureError(stage: string, err: unknown): void {
  const now = performance.now();
  const message = errorMessage(err);
  const key = `${stage}:${message}`;
  if (key === engine.lastCaptureErrorKey && now - engine.lastCaptureErrorPerf < CAPTURE_HEALTH_INTERVAL_MS) return;
  engine.lastCaptureErrorKey = key;
  engine.lastCaptureErrorPerf = now;
  telemetry.log("capture_error", { stage, message, ...captureHealthDetail() });
  // This is the event we need when the timer dies and no twentieth event follows it.
  telemetry.flush();
}

/**
 * The part of a `SessionOut` the stop path reads.
 *
 * `leave` and `finalise` both answer a `SessionOut`, which has always carried `state`;
 * #286 is the first caller to read it. Partial because a response that does not say must
 * read as unknown, and the retry controller only needs these two fields.
 */
type FinaliseResponse = Partial<Pick<SessionOut, "state" | "pending">>;

// ---------------------------------------------------------------------
// staying awake
// ---------------------------------------------------------------------

/*
 * A locked screen stops the microphone, and no web API can change that.
 *
 * On iOS the page is suspended when it stops being frontmost; capture stops and there is no
 * background-audio permission a web page can hold. Android Chrome usually keeps a tab with
 * an active capture alive, but OEM battery managers routinely disagree. So the only reliable
 * move is not to survive the lock -- it is to stop the screen locking in the first place.
 *
 * `navigator.wakeLock` does that. It is released whenever the page is hidden, so it has to be
 * re-taken on the way back, and it cannot resist the user pressing the power button. When it
 * is unavailable the honest thing is to say so, which is why this reports its state rather
 * than failing quietly.
 */
async function acquireWakeLock(): Promise<string> {
  if (!("wakeLock" in navigator)) return "unsupported";
  if (engine.wakeLock && !engine.wakeLock.released) return "held";
  try {
    engine.wakeLock = await navigator.wakeLock.request("screen");
    engine.wakeLock.addEventListener("release", () => {
      engine.wakeLock = null;
    });
    return "held";
  } catch (err) {
    // Thrown when the document is hidden, or on a battery-saver refusal.
    return `refused: ${(err as Error).message}`;
  }
}

async function releaseWakeLock(): Promise<void> {
  try {
    await engine.wakeLock?.release();
  } catch {
    /* Already gone. */
  }
  engine.wakeLock = null;
}

/**
 * Capture rebuild retries after a dead context (C7): bounded, visible-clock
 * only, and superseded by stop or a fresh recording. Three attempts over
 * ~30 s; on exhaustion the page stays functional (claims still flush, stop
 * still works) and says why, rather than failing the recording client-side.
 */
const CAPTURE_REBUILD_MAX_ATTEMPTS = 3;
const CAPTURE_REBUILD_BACKOFF_MS = [5000, 10000, 15000];

/**
 * One reporting path for every gap detector (suspension, delivery watchdog,
 * rebuild audit): telemetry with the detecting cause, a participant sentence,
 * and the persistent store flag the recording screen banners on.
 */
function reportCaptureGap(
  gap: { start: number; end: number; cause?: string },
  copy: string,
): void {
  telemetry.log("mic_gap", {
    gap_ms: Math.round((gap.end - gap.start) * 1000),
    cause: gap.cause ?? "suspended",
  });
  patch({ capturePause: { gapSec: gap.end - gap.start, atMs: Date.now() } });
  say(copy, "warn");
}

/**
 * Coming back from a lock or an app switch.
 *
 * Reconciling first matters more than re-arming the lock: whatever was missed is already
 * missed, and the recording has to be told about the hole before another claim is generated
 * over a timeline that no longer matches the clock.
 */
async function onVisible(): Promise<void> {
  if (document.visibilityState !== "visible") return;

  if (!engine.recorder) return;

  await engine.recorder.resumeIfSuspended();
  if (captureNeedsRebuild()) {
    // A dead context cannot reconcile: hand the whole resume to the rebuild
    // lane, which audits the hole itself before rebasing the clocks.
    if (engine.captureRebuildTimer === null) void rebuildCapture(0);
    await acquireWakeLock();
    return;
  }
  const gap = engine.recorder.reconcile();
  await acquireWakeLock();

  if (gap) {
    // Milliseconds, not rounded seconds. [V] Every gap a real session has produced is
    // ~0.50 s -- `CAPTURE_GAP_MIN_SEC` exactly, never more -- so rounding to seconds
    // logged all five of them as `1` and threw away the only digit that distinguishes a
    // deficit tripping the threshold from a microphone that genuinely stopped.
    const lost = Math.round(gap.end - gap.start);
    reportCaptureGap(
      gap,
      t("session.gap_background", { duration: durationWords(lost) }),
    );
  }
}

/** Whether the recorder's capture side is dead while the recording lives on. */
function captureNeedsRebuild(): boolean {
  const recorder = engine.recorder;
  if (!recorder || !engine.session) return false;
  const state = recorder.ctx?.state ?? null;
  return state === null || state === "closed";
}

async function rebuildCapture(attempt: number): Promise<void> {
  engine.captureRebuildTimer = null;
  const recorder = engine.recorder;
  if (!recorder || !engine.session) return;
  if (document.visibilityState !== "visible") return;
  const gapsBefore = recorder.gaps.length;
  try {
    await recorder.restartCapture();
  } catch (err) {
    logCaptureError("capture-rebuild", err);
  }
  await acquireWakeLock();
  if (!captureNeedsRebuild()) {
    for (const gap of recorder.gaps.slice(gapsBefore)) {
      const lost = Math.round(gap.end - gap.start);
      reportCaptureGap(
        gap,
        t("session.gap_resumed", { duration: durationWords(lost) }),
      );
    }
    return;
  }
  if (attempt + 1 >= CAPTURE_REBUILD_MAX_ATTEMPTS) {
    say(
      t("session.dropped_out"),
      "err",
    );
    return;
  }
  engine.captureRebuildTimer = setTimeout(
    () => void rebuildCapture(attempt + 1),
    CAPTURE_REBUILD_BACKOFF_MS[attempt] ?? 15000,
  );
}

/**
 * The recording lives only in this page's memory, so a reload loses the un-posted tail of
 * the conversation. Registered once and gated on the recorder, exactly as before.
 */
function guardUnload(e: BeforeUnloadEvent) {
  if (engine.recorder) e.preventDefault();
}

// ---------------------------------------------------------------------
// recording
// ---------------------------------------------------------------------

/**
 * How long setup waits for the first captured audio while the page is in view (#658).
 *
 * The first frame normally lands a few milliseconds after the context resumes. A context
 * that never runs (Safari's suspended-context failure, a microphone another app holds) would
 * otherwise leave "Setting up the meeting…" up forever, the shape #643 removed from boot. A
 * hidden page does not use this up: its timer waits for the page to come back, then gives it
 * `FIRST_AUDIO_GRACE_MS` after another resume.
 */
const FIRST_AUDIO_TIMEOUT_MS = 15_000;
const FIRST_AUDIO_GRACE_MS = 5_000;

/** The microphone opened and no audio arrived in time. Shown as the microphone message. */
export class CaptureStartError extends Error {
  constructor() {
    super("no audio arrived from the microphone");
    this.name = "CaptureStartError";
  }
}

function setupAborted(): Error {
  // Never shown: a cancelled start is not an error any screen reports (`isSetupAbort`).
  return new DOMException("setup_cancelled", "AbortError");
}

function isSetupAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/** Resolve on the recorder's first captured frame; see `FIRST_AUDIO_TIMEOUT_MS`. */
function awaitFirstAudio(recorder: InstanceType<typeof audio.Recorder>, signal: AbortSignal): Promise<void> {
  // A recorder with no signal to give (a test double) has nothing to wait for.
  if (!recorder.firstAudio || recorder.workletMessages > 0) return Promise.resolve();
  const firstAudio = recorder.firstAudio;
  return new Promise<void>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (settle: () => void) => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      signal.removeEventListener("abort", onAbort);
      settle();
    };
    const arm = (ms: number) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // Hidden: a suspended context is expected there, not a failure. Re-armed on return.
        if (document.visibilityState === "hidden") return;
        finish(() => reject(new CaptureStartError()));
      }, ms);
    };
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      recorder.ctx?.resume().catch(() => {});
      arm(FIRST_AUDIO_GRACE_MS);
    };
    const onAbort = () => finish(() => reject(setupAborted()));
    if (signal.aborted) {
      onAbort();
      return;
    }
    document.addEventListener("visibilitychange", onVisibility);
    signal.addEventListener("abort", onAbort, { once: true });
    firstAudio.then(() => finish(resolve));
    arm(FIRST_AUDIO_TIMEOUT_MS);
  });
}

/** How a Start or Join is set up: who may cancel it, when it was tapped, who to tell. */
export interface SetupOptions {
  /** Aborts the start before the first audio. The engine's own `cancelSetup` does too. */
  signal?: AbortSignal;
  /** `performance.now()` at the tap, for `setup_ms`. Defaults to when the request began. */
  tappedAt?: number;
  /** Called once the session exists and setup has begun, so the caller can show it. */
  onSetup?: () => void;
}

/**
 * Cancel a Start or Join that has not reached its first audio yet (#658).
 *
 * The session screen's Cancel, and leaving it during setup. Nothing is recorded by then;
 * the start in progress tears its microphone down and leaves the session (`abandonStart`).
 */
export function cancelSetup(): void {
  engine.setupAbort?.abort();
}

/**
 * Refuse a start before any session exists when the microphone is already blocked (#658).
 *
 * The permission prompt itself can only come with the microphone, after the session is
 * created; but a site whose microphone was denied earlier says so here, and then nothing is
 * created to leave behind. A browser without the Permissions API, or one that will not
 * answer for the microphone, is simply asked as before.
 */
async function assertMicNotBlocked(): Promise<void> {
  let state: PermissionState | null = null;
  try {
    const status = await navigator.permissions?.query({ name: "microphone" as PermissionName });
    state = status?.state ?? null;
  } catch {
    /* No answer for the microphone in this browser. */
  }
  if (state === "denied") throw new DOMException("microphone_blocked", "NotAllowedError");
}

/** A fresh setup controller, aborted by `cancelSetup` and by the caller's own signal. */
function beginSetup(signal?: AbortSignal): AbortSignal {
  const controller = new AbortController();
  engine.setupAbort = controller;
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  return controller.signal;
}

/**
 * Undo a start that failed or was cancelled after the session existed (#658).
 *
 * The microphone closes first, the screen moves on (`left` takes the session screen home),
 * and then the server is told. The creator abandons the session (#663): it ends at once,
 * notifies nobody and is left out of Past meetings, because nobody had a meeting. The
 * server refuses that once audio, claims or a guest have arrived; the creator cannot
 * discard (a host's only way out is ending the meeting), so a refused abandon ends the
 * meeting for everyone instead. A guest, who joined a real meeting, falls back to
 * Discard's two calls, since nothing was recorded and nothing should be kept. Best-effort
 * on the network: the screen never waits on it to let the person go.
 */
async function abandonStart(sessionId: string, { creator }: { creator: boolean }): Promise<void> {
  engine.recordingGeneration += 1;
  engine.setupAbort = null;
  engine.eventAbort?.abort();
  engine.eventAbort = null;
  await teardown().catch(() => {});
  engine.session = null;
  patch({ phase: "left", session: null, recording: false, settingUp: false, processingStage: null });
  if (creator) {
    try {
      await api.abandonSession(sessionId);
      return;
    } catch {
      // The creator cannot discard: a host's only way out is ending the meeting for
      // everyone, so a refused abandon ends it rather than leaving an open session
      // with no one able to close it. `abandon` should already have succeeded -- this
      // runs only when a guest joined or audio arrived during the setup window.
      await api.finaliseSession(sessionId).catch(() => {});
      return;
    }
  }
  try {
    await api.discardAudio(sessionId);
  } catch {
    /* Nothing was uploaded; there is nothing to delete. */
  }
  try {
    await api.leaveSession(sessionId);
  } catch {
    /* The server's sweep closes a session nobody is in. */
  }
}

export async function startRecording(session: SessionHandle, setup: SetupOptions = {}): Promise<void> {
  // Re-checked here, after the create/join await: that await is the window in which a
  // concurrent sign-in can install a conflicting account, and this is the last point
  // before the recorder starts (#38).
  assertRecordingIdentity();
  assertRecordingAllowed();
  const signal = setup.signal ?? beginSetup();
  if (signal.aborted) throw setupAborted();
  // A new recording supersedes every pending report from the previous one. The controller
  // suppresses its late response, while clearing the reference prevents it being reused for
  // the new session.
  engine.finaliseRetry?.cancel();
  engine.finaliseRetry = null;
  const app = useApp.getState();
  engine.session = session;
  telemetry.bindTrace(session.session_id);
  engine.chunkCursor = 0;
  engine.claimedSec = 0;
  engine.windows = 0;
  // From the create/join response, so the recording screen lists the devices straight
  // away rather than waiting up to three seconds for the first state poll.
  patch({ roster: rosterFrom((session as { roster?: unknown }).roster) });
  // The template is read once here rather than per pass: it does not change during a session,
  // and the scorer holds it for the whole of it.
  engine.scorer = app.template
    ? claims.createRollingScorer(Float32Array.from(app.template.centroid))
    : null;
  engine.stopping.release();
  engine.reported = false;
  engine.lastStateCheck = 0;
  // The audio socket's advisory `session_state` frame routes to the same handler
  // as the SSE `state_change` and the state poll (#272 L4b). Registered rather
  // than imported so `stream-session` -- which `session.ts` imports -- does not
  // have to import back. Cleared in `teardown`.
  setStreamSessionStateHandler((event) => onSessionState({ state: event.state }));
  engine.claimsRefused = 0;
  engine.wrapAt = -1;
  engine.deltaGate = null;
  // A new recording is a new timeline: a fresh epoch, sequences from 1, and no decision
  // carried over from the last session about which payload protocol this device speaks.
  engine.deltaOutbox = null;
  engine.deltaSessionId = null;
  // Before anything else in the reset: every in-flight decision from the previous
  // recording is invalidated by this line alone, whatever stage it had reached.
  engine.recordingGeneration += 1;
  // A new recording is a new timeline; the last one's epoch, anchor and stream state
  // must not carry into it. Deliberately *after* the generation bump and not before:
  // this arms the producer-readiness latch, and a latch keyed to the outgoing
  // generation would answer the question this recording is about to ask with the
  // previous recording's verdict (#170-C).
  resetStreamForNewRecording(engine.recordingGeneration);
  engine.deltaMode = false;
  engine.deltaDecided = false;
  engine.timelineEpoch = sign.newNonce();
  // The post-ready grace opens with the question live for this recording, whatever the
  // last one's Stop closed (#170-D). The window itself needs no clearing -- its identity
  // carries the epoch assigned on the line above -- but the *closed* flag is not identity
  // and would otherwise deny this recording a window it never used.
  contextGraceClosed = false;
  engine.deltaCounter = 0;
  engine.deltaCarry = [];
  engine.deltaHalted = false;
  engine.deltaHoldReported = "";
  engine.deltaAbandoned = 0;
  engine.deltaAbandonedBytes = 0;
  deltaModeDecision = null;
  engine.isCreator = session.creator_claim_profile_id === app.credentials?.claim_profile_id;
  stopChirping();

  patch({
    session,
    phase: "recording",
    recording: false,
    // "Setting up the meeting…" until the first audio (#658); see `awaitFirstAudio`.
    settingUp: true,
    reported: false,
    elapsedSec: 0,
    claimedSec: 0,
    windows: 0,
    isCreator: engine.isCreator,
    chirping: false,
    wrap: null,
    result: null,
    resultSessionId: null,
    processingStage: null,
  });
  say("");
  setup.onSetup?.();

  engine.lastCaptureHealthPerf = -Infinity;
  engine.lastCaptureErrorKey = "";
  engine.lastCaptureErrorPerf = -Infinity;
  engine.recorder = new audio.Recorder();
  let caps: audio.CaptureCapabilities;
  try {
    caps = await engine.recorder.start();
    // A Cancel pressed while the permission prompt was up lands here.
    if (signal.aborted) throw setupAborted();
    await awaitFirstAudio(engine.recorder, signal);
  } catch (err) {
    if (!isSetupAbort(err)) logCaptureError("start", err);
    throw err;
  }
  const tappedAt = setup.tappedAt;
  if (tappedAt !== undefined) {
    // Tap to first audio, the model wait and the request included: what the person waited.
    const setupMs = Math.round(performance.now() - tappedAt);
    telemetry.log("session_setup", { setup_ms: setupMs }, setupMs);
  }

  // Additive shadow evidence only. The existing recorder, claim, alignment,
  // merge and summary paths do not consume this measurement.
  telemetry.measureClockSync(engine.recorder.captureClockSample()).catch(telemetry.reportClockSyncError);

  const warnings = audio.captureWarnings(caps);
  if (warnings.length) say(warnings[0], "warn");
  // The probe's answer almost never moves on one device, so an unchanged PATCH
  // is skipped (#438). The marker is written on the server's answer: a failed
  // PATCH is retried at the next start, not forgotten.
  const capabilityDevice = app.credentials?.claim_profile_id;
  if (capabilitiesChanged(caps, capabilityDevice)) {
    api
      .updateCapabilities(caps)
      .then(() => markCapabilitiesReported(caps, capabilityDevice))
      .catch(() => {});
  }

  const held = await acquireWakeLock();
  if (held !== "held") {
    // Not a detail to hide: without it, a screen lock ends this device's recording and
    // there is nothing the page can do about it once it happens.
    say(
      t("session.screen_awake"),
      "warn"
    );
  }
  await store.saveLastSession({ sessionId: session.session_id, joinCode: session.join_code });

  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("beforeunload", guardUnload);
  engine.timer = setInterval(tick, 500);

  if (engine.eventAbort) engine.eventAbort.abort();
  engine.eventAbort = new AbortController();
  // Marked down here and up by the stream's callback only after the server's
  // initial `connected` event is parsed. Until then the poll stays fast because
  // HTTP headers alone do not prove the session subscription is receiving.
  engine.sseConnected = false;
  const eventGeneration = engine.recordingGeneration;
  const sseTelemetry = createSessionEventTelemetry(telemetry.log, telemetry.flush);
  api.streamSessionEvents(
    session.session_id,
    (
      evt: { event?: string; state?: string; roster?: unknown; session_sequence?: number },
      metadata?: { reconnectCount?: number; backoffMs?: number | null }
    ) => {
      if (engine.recordingGeneration !== eventGeneration) return;
      // Record arrival before a terminal state starts wrap-up and closes this reader.
      sseTelemetry.onEvent(evt, metadata);
      // The roster arrives on the same event as the state since #440: a member who
      // joins mid-recording reaches the screen through the stream, not the poll.
      if (evt.state) onSessionState({ state: evt.state, roster: evt.roster });
      // The delta writer's view of how far the session's durable rows have got. Only
      // `state_change` and `result_ready` are *read* here now: there is no live
      // transcript, so no turn, offset or snapshot events to fold (#358). A snapshot
      // from a server that still sends one is not a durable row, so it is not counted.
      if (evt.event !== "merged_timeline" && typeof evt.session_sequence === "number") {
        engine.deltaOutbox?.noteSessionSequence(evt.session_sequence);
      }
      if (evt.event === "result_ready") {
        void pollResult();
      }
    },
    engine.eventAbort.signal,
    (connected: boolean, metadata?: { reason?: string; status?: number; reconnectCount?: number }) => {
      if (engine.recordingGeneration !== eventGeneration) return;
      engine.sseConnected = connected;
      sseTelemetry.onConnectionChange(connected, metadata);
    }
  );

  // The last moment a Cancel can still undo the start: past this the meeting is live.
  if (signal.aborted) throw setupAborted();
  engine.setupAbort = null;
  const recordingStartedAtMs = performance.now();
  engine.recordingStartedAtMs = recordingStartedAtMs;
  // A new recording supersedes any stall banner from the previous one. Audio is flowing, so
  // setup is over: the screen says "Meeting in progress" and the clock runs from here.
  patch({ recording: true, settingUp: false, capturePause: null });
  telemetry.log("record_start");
  logCaptureHealth("start", true);

  if (streamProbeManualMode()) {
    telemetry.log("stream_arm", { mode: "manual" });
    // No socket is opened automatically here, so no producer will be admitted unless
    // somebody opens one by hand. Settling now rather than letting the deadline do it
    // means the claim path reports the URL flag it is actually looking at instead of
    // waiting fifteen seconds to report a timeout (#170-C).
    settleStreamReadiness(engine.recordingGeneration, "manual");
  } else {
    telemetry.log("stream_arm", { mode: "auto" });
    // The realtime pipeline, if this deployment enables it. After `recording: true`, because
    // `startStream` refuses to open a socket for a session that is not recording -- and
    // deliberately *not* awaited: asking the server about a flag and opening a WebSocket
    // are not things a recording should wait behind. A refused or slow gateway costs
    // this device its realtime contribution and nothing else.
    void startStreamIfEnabled(engine.recorder, "auto", 1, recordingStartedAtMs);
  }
}

async function tick(): Promise<void> {
  try {
    await tickOnce();
  } catch (err) {
    // A failure here stops attribution on this device, so it has to be loud. `setInterval`
    // with an async callback turns every exception into an unhandled rejection that nothing
    // listens for; [M] two whole sessions recorded audio and posted *zero* claims because
    // this threw on its first line each time -- no state polls, no chunks, no error, and a
    // device that looked like it was working.
    logCaptureError("tick", err);
    if (engine.timer) clearInterval(engine.timer);
    engine.timer = null;
    say(
      t("session.voice_lost"),
      "err"
    );
  }
}

async function tickOnce(): Promise<void> {
  const recorder = engine.recorder;
  if (!recorder || !engine.session) return;

  logCaptureHealth("tick");

  // Also checked here, not only on `visibilitychange`: a phone call or another app grabbing
  // the microphone interrupts capture without the page ever being hidden.
  const gap = recorder.reconcile();
  if (gap) {
    reportCaptureGap(
      gap,
      t("session.mic_stopped", { duration: durationWords(Math.round(gap.end - gap.start)) }),
    );
  }

  const elapsed = recorder.seconds;
  patch({ elapsedSec: elapsed });

  // Has whoever created this session ended it? Throttled off the same tick rather than
  // given its own interval, so there is one clock to reason about.
  if (elapsed - engine.lastStateCheck >= statePollEverySec(engine.sseConnected)) {
    engine.lastStateCheck = elapsed;
    api
      .getSession(engine.session.session_id)
      .then(onSessionState)
      .catch(() => {
        /* A missed poll is nothing: the next one is 3 s away, and a claim response carries
           the same field. */
      });
  }

  // Scoring runs every tick now, on whatever window's audio has just completed, rather than
  // saving a chunk's worth for the moment somebody presses stop. Same windows, same total
  // work -- see `createRollingScorer` for what that cost the phone it was measured on.
  //
  // One mutex over both the rolling pass and the chunk harvest, because both touch the scorer
  // and because the stop path waits on exactly this flag before taking the last of it.
  if (!scoring) {
    scoring = true;
    try {
      // Decide once per session before any claims are emitted. A device that
      // cannot establish the delta lease records no browser claim payload.
      //
      // Deliberately *ahead* of the rolling pass. `advance()` is what grows the scorer's
      // cache, so scoring first and asking about capacity afterwards leaves the one
      // buffer nothing bounds: the queue stops taking work, the carry stops draining,
      // and the scorer keeps embedding windows into a Map that only ever grows. On a
      // long outage that is the rest of the session held in memory on a device.
      await ensureDeltaMode();
      if (engine.deltaMode && deltaScoringAllowed()) {
        const done = await engine.scorer?.advance(readFrom(recorder), elapsed);
        if (done) {
          engine.windows += done;
          patch({ windows: engine.windows });
        }
      }
      if (elapsed - engine.chunkCursor >= K.CLAIM_CHUNK_SEC) {
        await scoreAndQueueChunk(engine.chunkCursor, elapsed);
      }
      // Continuous emission. This is the whole point of the delta path: a harvest becomes
      // a signed claim on the 500 ms tick rather than waiting out the 20 s harvest cadence.
      if (engine.deltaMode) pumpClaimDeltas();
    } catch (err) {
      // Scoring, not delivery: the span was never harvested. Windows already scored are
      // still cached, so the next tick picks up where this one stopped.
      logCaptureError("scoring", err);
      say(t("session.scoring_error"), "warn");
    } finally {
      scoring = false;
    }
  }

  // Delivery is not scoring's problem. Delta delivery is kicked every tick so a
  // claim that could not be posted when it was built is retried promptly.
  void engine.deltaOutbox?.flush();
}

/**
 * This device was signed out while it was recording.
 *
 * Someone marks a device as lost from a device they still have, and this is that device.
 * Without this branch a revoked device falls into "a claim chunk failed to upload… still
 * recording" and keeps going forever -- microphone live, wake lock held, a rejected chunk
 * every ~20 s, and its owner told the *upload* is flaky.
 *
 * Everything here is local. `leave` and `finalise` authenticate with the same dead token, so
 * calling them would 401 too -- there is nothing to report to the server and nothing it can
 * do about it.
 *
 * Storage is deliberately left alone. The template is still this person's voice and the
 * token being dead does not make it wrong; re-registering mints a fresh device id and
 * keypair anyway, so nothing has to be destroyed first. If this 401 were ever spurious,
 * wiping here would have cost an enrolment for a server-side blip. `deviceRevoked` is what
 * routes this device back to setup without touching what is stored.
 *
 * The recorder is torn down *before* the flag flips, because the flag makes the route guard
 * unmount this screen -- and an unmount that raced the teardown would leave a live
 * microphone with nothing holding it.
 */
async function deviceSignedOut(detail: string): Promise<void> {
  await teardown();
  engine.reported = true;
  patch({ reported: true });
  engine.session = null;
  patch({ phase: "revoked", session: null, deviceRevoked: true, processingStage: null });
  sayOn(
    "/enroll",
    t("session.signed_out", { detail }),
    "err"
  );
}

/**
 * React to the session state the server reports.
 *
 * `closing` is the creator saying the conversation is over. Every other device stops,
 * flushes what it has, and reports in -- which is what lets the merge wait for them instead
 * of reading the table while they are still talking.
 */
function onSessionState(session: { state?: string; roster?: unknown }): void {
  // Before the guard below, deliberately. That guard is about *wrap-up transitions* --
  // it exists so a late poll cannot re-trigger one -- and the roster is not a
  // transition. Recording the names first means a member who joined moments before Stop
  // is still listed.
  if (session.roster !== undefined) {
    const roster = rosterFrom(session.roster);
    // Only when it says something. An endpoint that answered with an empty list, or a
    // body that failed to parse, must not blank the devices the screen is already listing.
    if (Object.keys(roster).length) patch({ roster });
  }
  if (!engine.session || engine.stopping.busy || !engine.recorder) return;
  if (session.state === "closing") {
    // On the creator, a `closing` it did not start is End from another device of the
    // account that pays for it (#729, #735) -- this device's own Stop holds the latch
    // above. It is this device's own stop all the same: `finishSession` sends the final
    // watermark and the creator's `finalise`, so the audio up to End is kept and the
    // meeting does not wait out the close grace for a device that is still here.
    void wrapUp(engine.isCreator ? t("session.ended_on_your_other_device") : t("session.host_ended"));
  } else if (session.state === "finalising" || session.state === "complete") {
    void wrapUp(t("session.meeting_has_ended"));
  }
}

/** Adapt the recorder to what the scorer wants: give me this slice of the recording. */
const readFrom = (recorder: NonNullable<typeof engine.recorder>) =>
  (fromSec: number, toSec: number): Float32Array => recorder.slice(fromSec, toSec);

/** Score the completed portion of the recording and queue signed claim deltas. */
async function scoreAndQueueChunk(
  fromSec: number,
  toSec: number,
  { final = false }: { final?: boolean } = {}
): Promise<void> {
  const app = useApp.getState();
  const recorder = engine.recorder;
  if (!recorder || !engine.session || !app.template || !app.credentials || !app.keypair) return;

  // Only the span length is needed here; allocating a PCM copy was left over
  // from the retired batch scorer. The rolling scorer owns all inference.
  if (toSec - Math.max(0, fromSec - K.WINDOW_SEC) < 1) return;

  // If the producer lease never became available, do not harvest scored windows into
  // a protocol that has no server route. The cursor still advances so the recorder
  // cannot repeatedly retry the same span forever.
  if (!engine.deltaMode) {
    engine.chunkCursor = toSec;
    if (final) wrapBar(1);
    return;
  }

  // Whatever the rolling pass has finished. `FINAL_CHUNK_HOP_SEC` is gone with the burst it
  // existed to halve: there is no 20 s tail to coarsen any more, so the last seconds of a
  // session keep the same 1 s boundary precision as the rest of it.
  const t0 = performance.now();
  // Backpressure, checked *before* the scorer is drained. `harvest()` clears the windows
  // it returns, so discovering there is no room afterwards would mean deleting scored
  // speech -- which is the one thing a bounded queue must never cost.
  if (engine.deltaMode && !deltaHasCapacity()) {
    engine.chunkCursor = toSec;
    telemetry.log("claim_delta_backpressure", { waiting: deltaWaiting(), final });
    if (final) wrapBar(1);
    return;
  }
  const harvested = engine.scorer?.harvest(MAX_DELTA_HARVEST_WINDOWS, CLAIM_DELTA_SPLIT_SEC);

  // Where the rolling pass's cost is reported. Under continuous scoring the duration of *this*
  // function is nearly nothing, so on its own it would say the device was keeping up even while
  // it drowned. `backlog` is the number that answers that: windows whose audio has arrived and
  // which are still owed. Above zero and climbing means this device cannot sustain real-time.
  const roll = engine.scorer?.drainStats();
  const backlog = engine.scorer?.pending(toSec) ?? 0;

  // The chunk cadence has to keep moving even when there is nothing new to claim.
  engine.chunkCursor = toSec;
  if (!harvested) {
    telemetry.log("score", { windows: 0, final, backlog, ...roll }, performance.now() - t0);
    if (final) wrapBar(1);
    return;
  }

  const { frames, windows, fromSec: spanStart, toSec: spanEnd } = harvested;
  const intervals = claims.collapseToIntervals(frames);
  assertFiniteConfidences(intervals);

  // What the VAD would have gated out, and how much of it cleared the floor --
  // measured on a sample of chunks rather than all of them (#438).
  //
  // Not gating on `speechRegions` is measured as safe on a real recording -- 0 of 118 windows
  // were entirely non-speech, so there was no room tone to mis-score -- but that was one
  // conversation at 93.6% speech. The sampled VAD run keeps the open question fed:
  // `vad_rejected_claimed` above zero on a quiet session is the signal that silence
  // is being credited to somebody. Skipped chunks carry no `vad_*` keys at all,
  // so a skip never reads as a zero measurement.
  let vadDetail: Record<string, number> = {};
  if (vadDiagnosticDue()) {
    let rejected = 0;
    let rejectedClaimed = 0;
    const spanWav = recorder.slice(spanStart, spanEnd);
    if (spanWav.length >= K.SAMPLE_RATE) {
      const regions = audio.speechRegions(spanWav).map(([a, b]) => [a + spanStart, b + spanStart]);
      for (const [f, conf] of frames) {
        const at = f * K.FRAME_SEC;
        if (regions.some(([a, b]) => a <= at && at < b)) continue;
        rejected++;
        if (conf >= K.CLAIM_FLOOR) rejectedClaimed++;
      }
    }
    vadDetail = {
      vad_rejected_frames: rejected,
      vad_rejected_claimed: rejectedClaimed,
      vad_sampled: 1,
    };
  }
  telemetry.log(
    "score",
    {
      windows,
      final,
      backlog,
      span_sec: Math.round((spanEnd - spanStart) * 10) / 10,
      intervals: intervals.length,
      ...vadDetail,
      ...roll,
    },
    performance.now() - t0
  );
  if (final) wrapBar(1);

  queueClaimDeltas(recorder, frames, spanStart, spanEnd);
}

/* ------------------------------------------------------------------------- */
/* continuous claim deltas (#170-A)                                           */
/* ------------------------------------------------------------------------- */

/**
 * How long the wrap-up waits for the delta queue, in milliseconds.
 *
 * A device that cannot reach the network must not strand everyone else on a spinner. What is lost by
 * proceeding is the tail of this device's claims, not the session.
 */
const DELTA_DRAIN_MS = 45_000;

/**
 * The in-flight decision, so concurrent ticks ask once.
 *
 * Reset per recording alongside the rest of the delta state; a cached promise from the
 * last session would answer for this one.
 */
let deltaModeDecision: Promise<boolean> | null = null;
/** Which recording `deltaModeDecision` belongs to. */
let deltaDecisionGeneration = -1;

/**
 * How many times a *timed-out* lease read is retried before the answer is believed.
 *
 * [D] Four attempts over ~600 ms of backoff. This no longer bounds a still-opening
 * producer (#170-C): `producer_not_accepted` now returns immediately as a non-answer and
 * the 500 ms scoring tick is its retry cadence, so re-running this loop for it would put
 * `advance()` behind ~600 ms of sleeps on every tick and spend four requests where one
 * would do. A timeout keeps the loop because it has no other driver -- collapsing it to
 * one attempt would let a single slow request end delta mode outright.
 */
const PRODUCER_READINESS_ATTEMPTS = 4;
const PRODUCER_READINESS_BACKOFF_MS = 100;

/**
 * How long a `409 producer_not_accepted` is still allowed to mean "not yet" (#170-D).
 *
 * [V] Readiness and the context route are written by two different paths: the latch
 * settles on the gateway's own `ack`, while `GET /claim-deltas/context` reads the
 * producer row the stream ingress persists. So a 409 arriving *after* an accepted frame
 * is not necessarily "this device cannot use this context" -- it is often the second
 * write not having landed yet. Believing it there is the same mistake the transport gate
 * made one step earlier, and on a merge-off deployment it is the whole of the evidence:
 * the recording contributes no claims and looks healthy doing it.
 *
 * [D] 2 s, and bounded is the point. The alternative -- pending stays non-answer for the
 * whole recording -- turns the 500 ms scoring tick into an unbounded poll of `/context`,
 * which is exactly the design this path replaced. Two seconds is four ticks, which is
 * generous against a write that has already been acked, and it ends well inside the
 * 20 s before the first chunk harvest, so nothing is spent to get it.
 *
 * Costs stop nothing: every pass returns immediately, so the window is measured *across*
 * ticks rather than waited out inside one, and Stop closes it explicitly -- see
 * `closeContextGraceForStop`. Deliberately not justified by the latch:
 * `settleStreamReadiness` is first-answer-wins, so `recording_stopped` cannot displace
 * the `ready` this branch is reached under.
 */
const POST_READY_CONTEXT_GRACE_MS = 2_000;

/**
 * Which recording a post-ready context grace belongs to (#170-D).
 *
 * Three fields, because the generation alone is not an identity. [V] A generation bump is
 * what `startRecording` does, so it catches a new recording -- and misses the case
 * `isCurrentRecording` was built for: a rejoin that replaces the session while the engine
 * holds its generation. That leaves a window opened by the outgoing recording readable by
 * the incoming one, and because a window is only ever read to find out whether it has
 * *expired*, inheriting one is inheriting an expired one. The new recording's first `409`
 * would then settle `context_unavailable` on arrival -- exactly the believed-first-refusal
 * failure this whole window exists to remove, reintroduced on the handover path.
 *
 * So the identity is stated in full rather than inferred:
 *
 * - **`generation`**, for an ordinary new recording.
 * - **`sessionId`**, read from the store rather than from `engine`, because the store is
 *   what a rejoin replaces and `engine.recordingGeneration` is not. This is the same
 *   authority `isCurrentRecording` fences the stream lifecycle on, for the same reason.
 * - **`timelineEpoch`**, which is the uplink identity: a new transport timeline is a new
 *   producer to be admitted, so a window opened while waiting for a previous one has
 *   nothing to say about it. Redundant *today* and kept deliberately, in the same spirit
 *   as the chain re-check the old promotion gate made: the epoch is only ever assigned on
 *   the same line of `startRecording` that bumps the generation, so no test can vary it
 *   alone and none pretends to. It is here so that a future uplink re-arm which does not
 *   bump the generation is fenced by construction rather than by having been foreseen.
 *
 * Any of the three differing re-arms the window. Nothing mutates one of them in place,
 * so "differs" is the whole test.
 */
interface ContextGraceIdentity {
  generation: number;
  sessionId: string;
  timelineEpoch: string;
}

/** The open grace window, or `null` when none has been armed for anybody. */
let contextGrace: (ContextGraceIdentity & { startedAtMs: number }) | null = null;

/**
 * Whether Stop has closed the window for the recording that is ending (#170-D).
 *
 * Separate from clearing `contextGrace`, and that distinction is load-bearing.
 * `finishSession` forces one last decision *after* the tick is gone, precisely so a
 * recording whose producer lost the race to Stop reports a verdict instead of silently
 * contributing no claims. Clearing the window there would make that forced pass arm a *fresh*
 * one and return undecided -- the same invisibility, reached from the other direction. So
 * Stop closes the window rather than forgetting it.
 *
 * Cleared by `startRecording`, so the next recording opens with the question live.
 */
let contextGraceClosed = false;

/** The identity a grace armed right now would carry. */
function contextGraceIdentity(generation: number): ContextGraceIdentity {
  return {
    generation,
    sessionId: useApp.getState().session?.session_id ?? "",
    timelineEpoch: engine.timelineEpoch,
  };
}

function sameContextGrace(a: ContextGraceIdentity, b: ContextGraceIdentity): boolean {
  return (
    a.generation === b.generation &&
    a.sessionId === b.sessionId &&
    a.timelineEpoch === b.timelineEpoch
  );
}

/**
 * Close the post-ready grace because this recording is ending (#170-D).
 *
 * Called from both stop paths beside `stopStreamForRecordingEnd`, and both calls are
 * load-bearing at different moments. `finishSession`'s runs *before* the forced
 * `ensureDeltaMode`, which is the only place the closure can be observed: that pass must
 * answer rather than wait. `teardown`'s covers every other route to a stopped recording --
 * discard, sign-out, and the `finally` of a wrap-up that threw -- where nothing asks
 * again and the window's state is unobservable but should still be honest.
 */
function closeContextGraceForStop(): void {
  contextGraceClosed = true;
}

/**
 * What one pass at the producer lease learned.
 *
 * The distinction the transport gate turned on (#170-C). `producer_not_accepted` while
 * the stream socket is still opening and a genuine refusal are the same HTTP status and
 * meant opposite things: the first is "not yet" and must not be remembered, the second is
 * "not here" and must be. Collapsing both to `null` once cost a whole recording its
 * claims on nothing worse than a race.
 */
type ProducerReadiness =
  /** An accepted producer. This device can write deltas. */
  | { kind: "lease"; context: ClaimDeltaContext }
  /**
   * No answer yet -- the producer is still being admitted. Decide nothing.
   *
   * Before readiness this is unbounded, driven by the latch's own 15 s deadline. After
   * readiness it is bounded by `POST_READY_CONTEXT_GRACE_MS`, because at that point the
   * only thing still outstanding is one write catching up with an ack that has landed.
   */
  | { kind: "pending" }
  /**
   * A settled "no". `why` separates the two that must be reported differently:
   * `context_unavailable` is this recording's own answer, while `superseded` belongs to a
   * recording that no longer exists and is reported to nobody.
   */
  | { kind: "refused"; why: "context_unavailable" | "superseded" };

/**
 * The closed set of `claim_delta_mode` reasons, mirroring `ClaimDeltaModeDetail`.
 *
 * Server-side the detail is strict and `extra="forbid"`, and a detail that fails
 * validation takes the whole telemetry batch with it rather than being dropped alone. So
 * the enum is worth having in the type system on this side too: a typo here is a
 * compile error instead of every event in the flush disappearing.
 *
 * `producer_not_ready` is reserved and never emitted: a producer still being admitted
 * is the absence of an outcome, not one. See the server model.
 */
type ClaimDeltaModeReason =
  | "activated"
  | "activation_uncertain"
  | "flag_off"
  | "legacy_retired"
  | "manual"
  | "stream_disabled"
  | "producer_not_ready"
  | "readiness_timeout"
  | "stream_failed"
  | "context_unavailable"
  | "context_refused"
  | "activation_declined"
  | "recording_stopped";

/**
 * The HTTP status an error actually carries, or `null`.
 *
 * `null` rather than `0`, and the distinction is the point: a zero would be indexed and
 * charted as a real code. Out-of-range values are dropped for the same reason the
 * server bounds the field -- and because a detail it refuses costs every event beside it
 * in the same batch.
 */
function observedHttpStatus(err: unknown): number | null {
  const status = (err as { status?: number } | null)?.status;
  if (typeof status !== "number" || !Number.isFinite(status)) return null;
  const code = Math.trunc(status);
  return code >= 0 && code <= 599 ? code : null;
}

/**
 * One `claim_delta_mode` verdict, for the recording that asked (#170-C).
 *
 * Every emission is validated against both the generation and the session, without
 * exception -- including `activation_declined`, `activation_uncertain` and the deadline,
 * each of which can be reached by a callback that resolved after its recording ended.
 * A stale verdict is not merely useless: it is a second, contradictory row for a session
 * the live recording is about to report its own answer for, and downstream the two are
 * indistinguishable. Silent is the only correct output.
 *
 * `status` is omitted entirely unless one was actually observed.
 */
function logDeltaMode(
  generation: number,
  sessionId: string,
  on: boolean,
  reason: ClaimDeltaModeReason,
  status: number | null = null
): void {
  if (engine.recordingGeneration !== generation) return;
  if (engine.session?.session_id !== sessionId) return;
  telemetry.log("claim_delta_mode", status === null ? { on, reason } : { on, reason, status });
}

/**
 * Wall-clock bound on one claim-delta lease read.
 *
 * [D] 5 s, shorter than the marker's 8 s and deliberately so. The two failures are not
 * symmetric: giving up on a lease costs this recording its delta writer and falls back to
 * a path that works, while giving up on a marker leaves a session reported incomplete. A
 * decision that has to be made at the start of a conversation can afford to be decisive;
 * one made at the end cannot.
 */
const CLAIM_DELTA_REQUEST_DEADLINE_MS = 5_000;

/**
 * Choose the payload protocol for this session, once.
 *
 * Two conditions, both required, both fail-closed. The deployment must report
 * `claim_deltas_enabled` -- which is itself the conjunction of the ledger flag and
 * `stream_probe_enabled`, because a delta write has to quote an accepted producer -- and
 * this device must actually hold such a lease. Either missing means this device
 * contributes no claims, for the whole session.
 *
 * "For the whole session" is the load-bearing half. Once a sequence has started, a
 * transient failure must hold the signed queue rather than give up: the server's stop
 * barrier is already counting this device, and a writer that went quiet mid-conversation
 * would leave a ledger with a hole in it and a final watermark that never arrives.
 *
 * "Once" is now about the *answer*, not about the asking (#170-C). A pass that learned
 * only that the producer is still being admitted decides nothing and is not remembered,
 * so the tick asks again; what is decided once is the first `lease`, `refused` or
 * timeout, and after that nothing revisits it. The two ends of the window: a terminal
 * answer from the server, and the forced decision `finishSession` makes at Stop.
 */
/**
 * One pass at the producer lease, classified rather than collapsed (#170-C).
 *
 * `startStreamIfEnabled` is deliberately not awaited by `startRecording` -- a recording
 * must not wait behind a WebSocket. The consequence is that the first tick asks for a
 * lease while the socket is still opening and gets the ordinary `409
 * producer_not_accepted`. [V] The transport gate of 2026-08-26 proved what happened next:
 * that first refusal was believed, the legacy bundle path (since retired, #361) was
 * selected for the whole recording, and the session looked healthy while streaming
 * nothing. Admission landed at ~8.5 s against a ~0.6 s retry budget, so the fallback was
 * not a fallback -- it was the outcome.
 *
 * The bound was the wrong instrument. Any fixed budget is a guess against production
 * stream setup, and re-tuning the guess only moves the failure. So a still-opening
 * producer is reported as `pending` -- explicitly *not an answer* -- and the caller
 * leaves the decision open for the next tick to ask again. Only `lease` and `refused`
 * settle anything.
 *
 * `pending` returns on the first `producer_not_accepted` rather than after four: the
 * 500 ms tick is already the retry cadence, and looping here as well would spend four
 * requests and ~600 ms of sleeps per tick to learn the same thing.
 */
async function awaitProducerLease(
  sessionId: string,
  generation: number
): Promise<ProducerReadiness> {
  for (let attempt = 1; attempt <= PRODUCER_READINESS_ATTEMPTS; attempt += 1) {
    // Re-checked every pass: a recording that ended while this was waiting must not go on
    // to install a writer for a session nobody is in any more. `refused` rather than
    // `pending`, so no memoised decision is left behind for a recording that is over.
    if (engine.recordingGeneration !== generation) {
      return { kind: "refused", why: "superseded" };
    }
    try {
      // Bounded and cancellable (#170-B) [V]. `request` threads a signal to `fetch` but
      // never sets one of its own, so this await had no ceiling: a radio that has lost
      // the network without noticing produces a promise that never settles, and the
      // attempt loop below cannot bound it -- counting attempts requires attempts to end.
      // The whole recording would then sit with its payload protocol undecided.
      const context = await runWithDeadline(
        (options) => api.getClaimDeltaContext(sessionId, options),
        CLAIM_DELTA_REQUEST_DEADLINE_MS,
        "the claim-delta lease"
      );
      if (context && typeof context.producer_id === "string" && context.producer_id) {
        return { kind: "lease", context };
      }
      // A 200 with no producer in it is the server saying there is none, which is an
      // answer and not a wait.
      return { kind: "refused", why: "context_unavailable" };
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      const reason = (
        (err as { data?: { detail?: { error?: string } } } | null)?.data?.detail?.error ?? ""
      );
      const timedOut = (err as { timedOut?: boolean } | null)?.timedOut === true;
      const openingStill = status === 409 && reason === "producer_not_accepted";
      // The one refusal that means "not yet". Reported as a non-answer and, crucially,
      // not remembered: the caller leaves the protocol undecided and the next tick asks
      // again. Checked before the timeout branch, though the two cannot both be true.
      if (openingStill) return { kind: "pending" };
      // A timed-out lease read is retried here and then settles deterministically without
      // a lease, because unlike a still-opening producer it has no other driver. Settling
      // without one is safe *here* and only here: this is before activation, so nothing
      // durable exists and the server's barrier has never counted this device. After
      // activation there is no way out at all.
      if (timedOut && attempt < PRODUCER_READINESS_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, PRODUCER_READINESS_BACKOFF_MS * attempt));
        continue;
      }
      if (timedOut) return { kind: "refused", why: "context_unavailable" };
      // Anything else -- 404 from a deployment that does not do this, 403 for a device
      // with no membership, a refused admission -- is "not here". Thrown so the caller
      // records the status, and terminal.
      throw err;
    }
  }
  return { kind: "refused", why: "context_unavailable" };
}

async function decideDeltaMode(): Promise<boolean> {
  const app = useApp.getState();
  const session = engine.session;
  // Captured before the first await. Everything below re-checks it before touching
  // `engine`, so a decision belonging to a finished recording resolves inert.
  const generation = engine.recordingGeneration;
  // Captured alongside the generation, and for the same reason. Every `claim_delta_mode`
  // emission below is validated against both before it reaches the wire.
  const sessionId = session?.session_id ?? "";
  engine.deltaMode = false;
  // Set only on the "not yet" path, and the whole point of it: the `finally` below marks
  // the protocol chosen for the rest of the recording, and a pass that learned nothing
  // must not do that. See `ensureDeltaMode`, which drops its memo on the same condition.
  let undecided = false;
  try {
    // First, and ahead of the local preconditions below: this is the deployment's answer
    // rather than the device's, it does not depend on credentials, and it is by far the
    // commonest verdict of all -- so it is the one that must always be reported. Checked
    // second, it would go unreported on exactly the devices that had nothing else wrong.
    //
    // `=== true`, not truthiness: a missing field on an older deployment, or a config
    // request that never landed, must read as "off" rather than as permission.
    if (app.serverConfig?.claim_deltas_enabled !== true) {
      logDeltaMode(generation, sessionId, false, "flag_off");
      return false;
    }
    // Silent. These are local preconditions for a recording that has not properly
    // started, not an outcome for one that has, and no reason code describes them.
    if (!session || !app.credentials || !app.keypair) return false;

    // Readiness, asked of the stream lifecycle rather than inferred from a route
    // (#170-C). `streamReadiness` is a local read of a latch the socket settles when the
    // provider first accepts or consumes a producer frame -- no request, so a tick that
    // finds the question still open costs nothing and can simply ask again. The single
    // 15 s deadline lives with the latch, armed at `startRecording`.
    const ready = streamReadiness(generation);
    if (ready === null) {
      // Not an answer, so not a decision. `deltaDecided` stays clear and `ensureDeltaMode`
      // drops its memo, which is what lets the next tick see a producer that arrives at
      // 8.5 s instead of a verdict recorded at 0.5 s.
      undecided = true;
      return false;
    }
    if (ready !== "ready") {
      // Every other outcome is terminal, and the latch already named it: a dead socket,
      // a deployment with no realtime lane, manual mode, stop, or the deadline. The
      // vocabulary is shared with `claim_delta_mode` precisely so nothing is translated
      // here and nothing can drift.
      logDeltaMode(generation, sessionId, false, ready);
      return false;
    }

    // A producer is admitted, so the context read below is a single question with a
    // real answer -- not the polling loop that used to stand in for this latch.
    let readiness: ProducerReadiness;
    try {
      readiness = await awaitProducerLease(session.session_id, generation);
    } catch (err) {
      // A typed terminal refusal of the context after readiness: 404 from a deployment
      // without the route, 403 for a device with no membership. Separated from the outer
      // catch so the reason names the step -- everything past this point is activation,
      // and a failure there is not a refused context.
      logDeltaMode(generation, sessionId, false, "context_refused", observedHttpStatus(err));
      return false;
    }

    if (readiness.kind === "pending") {
      // A 409 after an accepted frame, given a bounded second chance (#170-D). The ack
      // and the producer row are two writes on two paths, so this is as likely to be the
      // second one still landing as it is to be a context this device cannot use -- and
      // the two are the same HTTP status. So the window decides, not the first answer.
      //
      // Re-armed whenever the window on hand belongs to somebody else -- a different
      // generation, session or timeline epoch. A window is only ever read to learn
      // whether it has *expired*, so inheriting one is inheriting an expired one, and the
      // first pass that notices is the one that fixes it.
      const nowMs = Date.now();
      const identity = contextGraceIdentity(generation);
      if (contextGrace === null || !sameContextGrace(contextGrace, identity)) {
        contextGrace = { ...identity, startedAtMs: nowMs };
      }
      // A wait is only a wait while something is still going to ask again, and Stop says
      // so explicitly rather than being inferred from the 500 ms tick being gone. [V] The
      // inferred form was unfalsifiable: with both in place, a mutation that made the
      // explicit close a no-op left every test passing, because the tick check silently
      // carried it. Two guards that mask each other are one guard and one blind spot.
      const willAskAgain = !contextGraceClosed;
      if (willAskAgain && nowMs - contextGrace.startedAtMs < POST_READY_CONTEXT_GRACE_MS) {
        // Not an answer, so not a decision -- the same discipline as a latch that has not
        // settled. `deltaDecided` stays clear, `ensureDeltaMode` drops its memo, and the
        // next tick asks again.
        undecided = true;
        return false;
      }
      // The window has closed with the context still refusing. *Now* it is this
      // recording's answer, and it is reported under the reason it always had.
      logDeltaMode(generation, sessionId, false, "context_unavailable");
      return false;
    }
    if (readiness.kind !== "lease") {
      // Readiness said there is a producer and the context does not have one for us.
      // Silent only for `superseded`, which belongs to a recording that no longer exists.
      const stale = readiness.why === "superseded";
      if (!stale) logDeltaMode(generation, sessionId, false, "context_unavailable");
      return false;
    }
    const context = readiness.context;
    if (engine.recordingGeneration !== generation) return false;
    // Activation is part of entering delta mode, not a step after it (#170-B).
    //
    // The server's stop barrier counts *activated* devices, so until this row exists the
    // merge has no reason to wait for this device -- and a device that activated only after
    // its first delta would be invisible to the barrier for the whole window in between.
    // Doing it here means the two facts move together: either this recording is a delta
    // recording and the barrier knows, or it contributes no claims and nothing durable
    // was created.
    const gate = new FinalWatermarkGate(makeGatePorts(session.session_id, generation));
    const activation = await gate.activateOnce(context);
    if (activation === "declined") {
      // A *definitive* refusal reached before anything durable could exist: the
      // deployment does not do this, or there is no accepted producer yet. Nothing was
      // written, so recording without claims is a genuine compatibility outcome rather
      // than a downgrade from a live writer.
      logDeltaMode(generation, sessionId, false, "activation_declined");
      return false;
    }
    if (activation === "uncertain") {
      // Never give up on an unresolved answer [V]. `activate` commits the barrier row
      // before writing its response, so a lost answer cannot prove the row is absent --
      // and going quiet on a session the server may already be counting is the one
      // failure this whole path exists to prevent: the merge would wait for a final
      // watermark that, having stopped writing, this device would never send. Proceeding as activated is the recoverable direction, and the append path
      // creates the row itself on the first accepted delta.
      logDeltaMode(generation, sessionId, true, "activation_uncertain");
    }

    // The last re-check, and the one that matters most: activation is a network round
    // trip, so this is where a back-to-back recording is most likely to have started. A
    // stale decision that reached here would install the previous recording's outbox,
    // gate and timeline epoch over the new one -- a writer signing for one session
    // against another session's ledger.
    //
    // The durable activation row it created is not a leak: it belongs to the *old*
    // session, whose barrier will wait for it and degrade it at the configured deadline
    // exactly as it does for any device that stopped without publishing.
    //
    // Silent, deliberately: this decision belongs to a recording that no longer exists,
    // and `claim_delta_mode` describes what a *live* recording chose. Emitting here would
    // put a second, contradictory verdict on the wire for a session the newer recording
    // is about to report its own answer for -- two rows, one true, indistinguishable
    // downstream. A stale decision resolves inert, including in telemetry.
    if (engine.recordingGeneration !== generation) return false;
    const box = makeDeltaOutbox(session.session_id, app.credentials.claim_profile_id as string);
    box.primeContext(context);
    engine.deltaOutbox = box;
    engine.deltaGate = gate;
    engine.deltaSessionId = session.session_id;
    engine.deltaMode = true;
    // The timeline epoch used to ride along here and no longer does: it is a
    // per-recording nonce, and the emitted line already carries `session_id`, so it was
    // an identifier in telemetry that bought nothing.
    logDeltaMode(generation, sessionId, true, "activated");
    return true;
  } catch (err) {
    // A 409 here is the ordinary answer for a device with no accepted stream producer,
    // and a 404 is a deployment that does not do this at all. Neither is a fault, and
    // both mean the same thing to this device: claim as it always has.
    // Everything the lease step did not already report: activation threw, or the writer
    // could not be built. `status` only if one was genuinely observed -- a fabricated
    // zero would be indexed and charted as a real HTTP code.
    logDeltaMode(generation, sessionId, false, "stream_failed", observedHttpStatus(err));
    return false;
  } finally {
    // Only for the recording that asked. A superseded decision marking `deltaDecided`
    // would tell the *new* recording that its protocol had already been chosen, and it
    // would run the whole session on a decision made for a different session.
    //
    // And only for a pass that actually decided (#170-C). Marking the protocol chosen on
    // a still-opening producer is the transport-gate defect itself: `false` would then be
    // the answer for the rest of the recording, however quickly the socket finished
    // opening afterwards.
    if (engine.recordingGeneration === generation && !undecided) engine.deltaDecided = true;
  }
}

/**
 * Exported for the lifecycle tests, which drive it directly.
 *
 * No behaviour change: the rolling tick is still the only caller in the app, and this is
 * the same idempotent, per-recording decision it has always made. Driving it by hand is
 * what lets a superseded-recording test be deterministic -- the alternative is waiting on
 * the tick scheduler, and a test whose interleaving depends on that is a test that passes
 * for whatever reason the scheduler supplies.
 */
export function ensureDeltaMode(): Promise<boolean> {
  if (engine.deltaDecided) return Promise.resolve(engine.deltaMode);
  // Keyed by generation as well as existence: `startRecording` clears this, but a
  // decision already in flight keeps running, and awaiting it would give the new
  // recording an answer computed for the old one.
  if (!deltaModeDecision || deltaDecisionGeneration !== engine.recordingGeneration) {
    deltaDecisionGeneration = engine.recordingGeneration;
    const decision = decideDeltaMode();
    deltaModeDecision = decision;
    // Dropped on an *undecided* resolution, and only then (#170-C). While a decision is
    // in flight the memo is what makes concurrent ticks ask once -- and what keeps one
    // recording to one activation. But a pass that resolved without choosing a protocol
    // must not be the answer the next tick reads: remembering `false` there is exactly
    // the memoised race this fix exists to remove. `deltaDecided` is the discriminator,
    // set by `decideDeltaMode`'s `finally` for every pass that did settle the question.
    void decision.finally(() => {
      if (deltaModeDecision === decision && !engine.deltaDecided) deltaModeDecision = null;
    });
  }
  return deltaModeDecision;
}

/**
 * Whether every claim delta this device produced has been acknowledged (#170-B).
 *
 * Read at the moment it is needed rather than remembered, so a second Stop press asks
 * the writer rather than a stale flag. All three conditions matter: the outbox must be
 * settled, the carry -- which lives outside the outbox -- must be empty, and nothing may
 * have been abandoned, because an abandoned piece is a claim that exists and was never
 * sent.
 */
function deltaWriterSettled(): boolean {
  return (
    (engine.deltaOutbox?.isSettled ?? true) &&
    engine.deltaCarry.length === 0 &&
    engine.deltaAbandoned === 0
  );
}

/**
 * Which recording the installed claim-delta writer belongs to. Read-only.
 *
 * A seam for the lifecycle tests, and read-only on purpose: the property worth asserting
 * is that a decision belonging to a finished recording never becomes the live writer, and
 * that is a question about identity rather than about traffic. Nothing in the app calls
 * this, and it changes no behaviour.
 */
export function claimDeltaWriterIdentity(): {
  sessionId: string | null;
  epoch: string;
  generation: number;
  active: boolean;
} {
  return {
    sessionId: engine.deltaSessionId,
    epoch: engine.timelineEpoch,
    generation: engine.recordingGeneration,
    active: engine.deltaMode,
  };
}

/** Everything this device has claimed but not yet had acknowledged. */
function deltaWaiting(): number {
  return engine.deltaCarry.length + (engine.deltaOutbox?.length ?? 0);
}

function deltaCarryBytes(): number {
  let total = 0;
  for (const draft of engine.deltaCarry) total += draftBytes(draft);
  return total;
}

/**
 * What the delta path is holding, queue and carry together.
 *
 * Exported because the bound is a combined one and neither half is observable from the
 * other: the outbox knows nothing about the carry, and a test that watched only the
 * queue would call an unbounded carry a pass. `abandoned` is the no-drop evidence --
 * pieces a terminal stop released rather than sent, counted after the buffers are gone.
 */
export function claimDeltaLoad(): {
  items: number;
  bytes: number;
  abandoned: number;
  abandonedBytes: number;
  halted: boolean;
} {
  return {
    items: deltaWaiting(),
    bytes: (engine.deltaOutbox?.bytes ?? 0) + deltaCarryBytes(),
    abandoned: engine.deltaAbandoned,
    abandonedBytes: engine.deltaAbandonedBytes,
    halted: engine.deltaHalted,
  };
}

/**
 * Hard ceiling on the carry, as a fail-closed assertion rather than a working bound.
 *
 * [D] 8. With `advance()` gated on capacity the carry cannot ordinarily exceed one
 * harvest's worth of split pieces, so reaching this means the gate did not hold. The
 * response is to stop the delta path, not to discard the pieces: dropping them would be
 * losing signed-in-all-but-name claims to protect a number, which is the trade this
 * whole path refuses.
 */
const MAX_DELTA_CARRY_ITEMS = 8;

/**
 * How many scored windows one harvest may take for the delta path.
 *
 * This is the preflight, and it is the only place the bound can actually be enforced:
 * `harvest()` is **destructive**, so by the time an oversized span is in hand the choice
 * is already between holding it and dropping it. Asking for at most one delta's worth
 * means the question never arises -- a catch-up pass that scored an hour of windows is
 * taken 55 s at a time, on successive ticks, and everything else stays in the scorer's
 * cache, which is a Map of numbers and costs nothing.
 *
 * Derived from the span limit rather than guessed: N windows on the hop grid cover
 * `(N - 1) * HOP_SEC + WINDOW_SEC` seconds, so this is the largest N that fits inside
 * one `CLAIM_DELTA_SPLIT_SEC` piece, and `queueClaimDeltas` therefore produces exactly
 * one draft per harvest in every ordinary case.
 */
const MAX_DELTA_HARVEST_WINDOWS = Math.max(
  1,
  Math.floor((CLAIM_DELTA_SPLIT_SEC - K.WINDOW_SEC) / K.HOP_SEC) + 1
);

/** Why the rolling pass may not run: the queue is full, or the writer is finished. */
type DeltaScoringState = "ok" | "full" | "closed";

function deltaScoringState(): DeltaScoringState {
  const box = engine.deltaOutbox;
  if (engine.deltaHalted || !box || box.isClosed) return "closed";
  if (engine.deltaCarry.length || !box.canAccept()) return "full";
  return "ok";
}

/**
 * Whether another harvest may be taken.
 *
 * The carry must be empty as well as the queue having room: the carry exists only when a
 * previous harvest was split into more pieces than the queue could take, and harvesting
 * on top of it would grow an unbounded second queue behind the bounded one.
 */
function deltaHasCapacity(): boolean {
  return deltaScoringState() === "ok";
}

/**
 * Whether the rolling scorer itself may run this tick, and the report if it may not.
 *
 * This is the bound. The queue and the carry each have their own limit, but neither
 * constrains the scorer, and the scorer is the buffer that actually grows: `advance()`
 * embeds every window whose audio has arrived and keeps the scores in a Map until
 * something harvests them. With delivery stopped and harvesting gated, that Map is the
 * one place a stalled session accumulates without limit -- an hour of windows on a
 * device, invisible until it is fatal.
 *
 * Pausing it is lossless. `advance()` tracks its own `nextToScore`, the recorder retains
 * the audio for the whole session, and nothing is discarded: when capacity returns the
 * pass resumes from exactly the window it stopped at and catches up in batches. What is
 * deferred is work, not evidence.
 *
 * `closed` is the terminal branch and is different in kind. A writer that has been
 * permanently refused, signed out, or fenced past repair will never send another delta,
 * so continuing to score for it accumulates memory for a queue with no exit. The path
 * stops and says so; recording and audio upload carry on, because the transcript does
 * not depend on this.
 */
function deltaScoringAllowed(): boolean {
  const state = deltaScoringState();
  if (state === "ok") {
    engine.deltaHoldReported = "";
    return true;
  }
  if (engine.deltaHoldReported !== state) {
    engine.deltaHoldReported = state;
    telemetry.log("claim_delta_scoring_paused", {
      reason: state,
      waiting: deltaWaiting(),
      carry: engine.deltaCarry.length,
    });
    if (state === "closed") {
      engine.deltaHalted = true;
      say(
        t("session.voice_unmatched"),
        "err"
      );
    } else {
      say(
        t("session.slow_connection"),
        "warn"
      );
    }
  }
  return false;
}

/** Stop the delta path for good, without discarding anything already queued. */
function haltDeltaWriter(detail: string): void {
  console.warn("Voice recognition stopped", detail);
  if (engine.deltaHalted) return;
  engine.deltaHalted = true;
  engine.deltaHoldReported = "closed";
  releaseDeltaCarry();
  telemetry.log("claim_delta_halt", {
    waiting: deltaWaiting(),
    abandoned: engine.deltaAbandoned,
    abandoned_bytes: engine.deltaAbandonedBytes,
  });
  say(
    t("session.voice_unmatched"),
    "err"
  );
}

/**
 * Give up the carry, having first written down exactly what was given up.
 *
 * Only ever reached once the writer is finished, and then it is the honest thing: these
 * pieces can never be sent, and each one holds an envelope, so keeping them costs the
 * device the memory this whole policy exists to bound. The count and the byte total
 * survive in `claimDeltaLoad()` and in the wrap-up report -- abandonment is stated, not
 * silent, which is the difference between a terminal stop and a drop.
 */
function releaseDeltaCarry(): void {
  if (!engine.deltaCarry.length) return;
  engine.deltaAbandoned += engine.deltaCarry.length;
  engine.deltaAbandonedBytes += deltaCarryBytes();
  engine.deltaCarry = [];
}

/** Seconds of speech one draft claims, for the on-screen meter. */
function claimedSecondsOf(draft: ClaimDeltaDraft): number {
  let total = 0;
  for (const iv of draft.intervals) total += Math.max(0, iv.end - iv.start);
  return total;
}

/**
 * Wire the marker gate to the API and the wrap-up display.
 *
 * `activate` and `publish` hand the body over verbatim -- the gate owns its immutability
 * and nothing here may reshape it.
 */
/**
 * Marker bounds overridden by a test, or `null` for the shipped defaults.
 *
 * A seam, not a knob: the wrap-up test for an unresolved publish has to prove that
 * `finishSession` *returns*, and waiting out the real 8 s attempt bound six times over
 * would make that test slower than the failure it guards against. Production reads the
 * defaults in `claim-delta-final-watermark.ts` and this stays null.
 */
let markerBoundsForTest: { attemptDeadlineMs?: number; maxAttempts?: number } | null = null;

export function setClaimDeltaMarkerBoundsForTest(
  bounds: { attemptDeadlineMs?: number; maxAttempts?: number } | null
): void {
  markerBoundsForTest = bounds;
}

function makeGatePorts(sessionId: string, generation: number) {
  const epoch = engine.timelineEpoch;
  return {
    ...(markerBoundsForTest ?? {}),
    // Frozen at decision time, not read live off `engine` (#170-B) [V]. `startRecording`
    // mints a new epoch, so a gate whose activation resolved late would otherwise sign
    // its marker against the *next* recording's epoch while addressing the previous
    // recording's ledger -- and the server would answer `device_state_missing` for a row
    // that exists under a different key.
    timelineEpoch: () => epoch,
    activate: (body: unknown, options: { signal: AbortSignal }) =>
      api.activateClaimDeltas(sessionId, body, { signal: options.signal }),
    publish: (body: unknown, options: { signal: AbortSignal }) =>
      api.postClaimDeltaFinalWatermark(sessionId, body, { signal: options.signal }),
    // The lease refresh a fenced-out marker performs. Bounded on its own account: it sits
    // inside the marker's retry ladder, and an unbounded refresh would reintroduce the
    // hang the marker deadline just removed, one level down.
    context: () =>
      runWithDeadline(
        (options) => api.getClaimDeltaContext(sessionId, options),
        CLAIM_DELTA_REQUEST_DEADLINE_MS,
        "the claim-delta lease"
      ),
    onSignedOut: (err: Error) => deviceSignedOut(err.message),
    onStage: (stage: string, detail?: string) => {
      telemetry.log("claim_delta_watermark", {
        stage,
        detail: detail ?? "",
        superseded: engine.recordingGeneration !== generation,
      });
    },
  };
}

function makeDeltaOutbox(sessionId: string, claimProfileId: string): ClaimDeltaOutbox {
  return new ClaimDeltaOutbox({
    claimProfileId,
    timelineEpoch: engine.timelineEpoch,
    // Signing happens here, at the head, and not when the draft was queued: the sequence
    // is inside the signed bytes, so binding it early would burn a sequence on a write
    // that never lands. See `claim-delta-outbox.ts`.
    sign: async (draft, sequence) => {
      const app = useApp.getState();
      const startedAt = engine.recorder?.startedAt;
      if (!app.keypair || !app.credentials || !startedAt) {
        throw new Error("cannot sign a claim delta without a keypair, credentials and a recording");
      }
      const payload = claims.buildClaimDeltaPayload({
        sessionId,
        claimProfileId,
        participantId: app.credentials.participantId as string,
        timelineEpoch: engine.timelineEpoch,
        deltaId: draft.deltaId,
        deltaSequence: sequence,
        claimId: draft.claimId,
        intervals: draft.intervals,
        envelopeBytes: draft.envelopeBytes,
        localStartSec: draft.localStartSec,
        localEndSec: draft.localEndSec,
        recordingStartedAt: startedAt.toISOString(),
        captureGaps: draft.captureGaps,
      });
      return sign.signClaimDeltaPayload(app.keypair, payload);
    },
    post: async (body) => {
      const t0 = performance.now();
      try {
        const out = await api.postClaimDelta(sessionId, body);
        telemetry.log("claim_delta_upload", { ok: true }, performance.now() - t0);
        return out;
      } catch (err) {
        telemetry.log("claim_delta_upload", { ok: false }, performance.now() - t0);
        throw err;
      }
    },
    resume: (body) => api.resumeClaimDeltas(sessionId, body),
    context: () =>
      runWithDeadline(
        (options) => api.getClaimDeltaContext(sessionId, options),
        CLAIM_DELTA_REQUEST_DEADLINE_MS,
        "the claim-delta lease"
      ),
    onAccepted: (accepted, draft) => {
      // The delta ACK carries no `claimed_sec` -- the ledger stores what was claimed
      // rather than recomputing it -- so the meter is fed from the intervals this device
      // signed. Same number, computed on the side that already knows it.
      engine.claimedSec += claimedSecondsOf(draft);
      patch({ claimedSec: engine.claimedSec });
      if (accepted.warnings?.length) say(accepted.warnings[0], "warn");
      if (accepted.session_state) onSessionState({ state: accepted.session_state });
    },
    onRetryable: () =>
      say(
        t("session.slow_connection"),
        "warn"
      ),
    // Not "dropped and carried on": delta sequences are contiguous, so a refusal that
    // cannot be repaired ends the writer -- and it is said plainly rather than hidden.
    onPermanent: (error) => {
      engine.claimsRefused += 1;
      // Claim the terminal report here rather than leaving it to the generic notice on
      // the next tick: this is the one place that knows *why*, and two messages saying
      // the same thing with only one of them naming the reason is worse than either.
      engine.deltaHalted = true;
      engine.deltaHoldReported = "closed";
      // The server's code or detail, never the signed payload. A permanent refusal
      // stops the claim writer but leaves the meeting running, so this is
      // diagnostics on a live session rather than a close.
      telemetry.log("claim_delta_halt", {
        waiting: deltaWaiting(),
        reason: claimDeltaRefusalReason(error),
      });
      say(
        t("session.voice_unmatched"),
        "err"
      );
    },
    onBackpressure: (waiting, bytes) => {
      telemetry.log("claim_delta_backpressure", { waiting, bytes });
      say(
        t("session.slow_connection"),
        "warn"
      );
    },
    onTerminal: () => wrapUp(t("session.ended_elsewhere")),
    onSignedOut: (err) => deviceSignedOut(err.message),
  });
}

/**
 * Split one harvested span into drafts no longer than the protocol allows, and queue them.
 *
 * The split is not hypothetical. Harvests are ordinarily a second or two, but the queue
 * pauses harvesting under backpressure, so the first harvest after a long outage covers
 * however much was scored while it lasted. A span over `MAX_CLAIM_DELTA_SPAN_SEC` is a 400
 * on a signed payload, which is unrecoverable -- no retry and no lease refresh repairs it.
 *
 * Pieces that do not fit go to the carry rather than being dropped, and harvesting is
 * blocked while the carry is non-empty.
 */
function queueClaimDeltas(
  recorder: NonNullable<typeof engine.recorder>,
  frames: Map<number, number>,
  spanStart: number,
  spanEnd: number
): void {
  const box = engine.deltaOutbox;
  if (!box || box.isClosed || engine.deltaHalted) return;

  // Preflight, before a single draft is built.
  //
  // The harvest is bounded by count *and* span, so this span is one piece and the loop
  // below runs once. Checking anyway is the difference between a bound and an assumption:
  // if a harvest ever arrives oversized, the drafts do not exist yet, and refusing to
  // build them is the only point at which the combined item and byte limit can still be
  // honoured. Afterwards the choice is already between holding them and dropping them.
  const pieces = Math.max(1, Math.ceil((spanEnd - spanStart) / CLAIM_DELTA_SPLIT_SEC));
  if (pieces > MAX_DELTA_CARRY_ITEMS) {
    haltDeltaWriter(
      `a ${Math.round(spanEnd - spanStart)}s harvest would split into ${pieces} claim ` +
        `pieces, past the ${MAX_DELTA_CARRY_ITEMS} this device will hold`
    );
    telemetry.log("claim_delta_oversized_harvest", {
      span_sec: Math.round(spanEnd - spanStart),
      pieces,
    });
    return;
  }

  let full = false;
  for (let from = spanStart; from < spanEnd - 1e-6; from += CLAIM_DELTA_SPLIT_SEC) {
    const to = Math.min(spanEnd, from + CLAIM_DELTA_SPLIT_SEC);
    if (to <= from + 1e-6) continue;

    // Frames belonging to this piece, so the intervals cannot start before the piece's
    // `local_start_sec` or run past its end -- both of which the server validates.
    const slice = new Map<number, number>();
    for (const [f, conf] of frames) {
      const at = f * K.FRAME_SEC;
      if (at >= from - 1e-6 && at < to - 1e-6) slice.set(f, conf);
    }
    const intervals = claims.collapseToIntervals(slice);
    assertFiniteConfidences(intervals);

    const n = ++engine.deltaCounter;
    const draft: ClaimDeltaDraft = {
      deltaId: `d${n}`,
      claimId: `c${n}`,
      intervals,
      // No energy envelope: the server aligns devices on envelopes it computes from their
      // archived audio (#362). The protocol field stays optional for older app versions.
      envelopeBytes: null,
      localStartSec: from,
      localEndSec: to,
      captureGaps: sliceGapsForDraft(recorder.gaps, from, to),
    };

    if (full || !box.push(draft)) {
      full = true;
      engine.deltaCarry.push(draft);
    }
  }

  // Fail closed, and keep what is here. Unreachable while the capacity gate holds --
  // which is exactly why it is worth asserting: if it ever fires, the gate has a hole
  // and the alternative to stopping is an unbounded second queue behind the bounded one.
  if (engine.deltaCarry.length > MAX_DELTA_CARRY_ITEMS) {
    haltDeltaWriter(
      `${engine.deltaCarry.length} split claim pieces could not be queued, past the ` +
        `${MAX_DELTA_CARRY_ITEMS} this device will hold`
    );
  }
}

/**
 * One continuous emission pass, run every tick.
 *
 * Carry first, then capacity, then harvest -- in that order, and the order is the whole
 * safety property: nothing is harvested until there is somewhere for it to go.
 */
function pumpClaimDeltas(): void {
  const box = engine.deltaOutbox;
  const recorder = engine.recorder;
  if (!box || box.isClosed || engine.deltaHalted || !recorder) return;

  while (engine.deltaCarry.length) {
    if (!box.push(engine.deltaCarry[0])) return;
    engine.deltaCarry.shift();
  }
  if (!box.canAccept()) return;

  // Bounded, because the harvest is destructive: taking more than one delta's worth
  // means holding it or dropping it, and there is no third option once it is in hand.
  // The rest stays in the scorer's cache and comes out on the next tick.
  const harvested = engine.scorer?.harvest(MAX_DELTA_HARVEST_WINDOWS, CLAIM_DELTA_SPLIT_SEC);
  if (!harvested) return;
  queueClaimDeltas(recorder, harvested.frames, harvested.fromSec, harvested.toSec);
}

// ---------------------------------------------------------------------
// wrap-up
// ---------------------------------------------------------------------

function wrapShow(): void {
  engine.wrapAt = -1;
  patch({ wrap: { key: WRAP_STEPS[0][0], fraction: null } });
}

function wrapHide(): void {
  patch({ wrap: null });
}

/** Mark a stage current; everything before it is done. Monotonic, so a late call from an
    earlier stage cannot walk the list backwards. */
function wrapStep(key: string, fraction: number | null = null): void {
  engine.wrapAt = nextWrapAt(engine.wrapAt, key);
  if (engine.wrapAt < 0) return;
  patch({ wrap: { key: WRAP_STEPS[engine.wrapAt][0], fraction } });
}

/** `null` means "running, remaining work unknown" -- an animated bar, not a fake number. */
function wrapBar(fraction: number | null): void {
  const current = useApp.getState().wrap;
  if (!current) return;
  patch({ wrap: { key: current.key, fraction } });
}

/** Release the microphone, the wake lock and every listener. Idempotent. */
async function teardown(): Promise<void> {
  if (engine.timer) clearInterval(engine.timer);
  engine.timer = null;
  if (engine.captureRebuildTimer) clearTimeout(engine.captureRebuildTimer);
  engine.captureRebuildTimer = null;
  // A report retry belongs to this recording. Teardown is also the unmount/discard seam,
  // so no delayed continuation may speak for a session that is no longer on this screen.
  engine.finaliseRetry?.cancel();
  engine.finaliseRetry = null;
  // Anything still in the carry is past its last chance -- the tick that would have
  // pumped it is gone -- so it is released here rather than held to the next session,
  // and counted as abandoned rather than quietly forgotten.
  releaseDeltaCarry();
  stopChirping();
  // Before the recorder, for the same reason as in `finishSession`, and here as well
  // as there because `teardown` is also the discard path and the `finally` of a
  // wrap-up that threw. Stopping an already-stopped stream is a no-op.
  closeContextGraceForStop();
  await stopStreamForRecordingEnd();
  // Settle the socket before tearing down over it (#272 L1). `stopStream` no
  // longer waits for the gateway's close, so without this a teardown reached by
  // an early `return` -- the watermark-failure branch in `finishSession`, or an
  // unmount -- would drop the engine's state while a socket was still draining.
  // Bounded at `CLOSE_WAIT_MS` by the uplink, and it never rejects.
  await awaitStreamClosed();
  // The advisory `session_state` frame has nowhere to go once this session is
  // gone, and a stale handler would be holding a closure over a torn-down engine.
  setStreamSessionStateHandler(null);
  if (engine.recorder) {
    await engine.recorder.stop().catch(() => {});
    engine.recorder = null;
  }
  engine.recordingStartedAtMs = null;
  patch({ recording: false, chirping: false, capturePause: null });
  await releaseWakeLock();
  document.removeEventListener("visibilitychange", onVisible);
  window.removeEventListener("beforeunload", guardUnload);
}

/** The button. Ends it for everyone if this device created the session. */
export const stopRecording = () => finishSession(null);

/** The creator ended it elsewhere: same wrap-up, not initiated here. */
const wrapUp = (note: string) => finishSession(note);

/**
 * Score the last stretch, upload, say "I am done", then wait for the result.
 *
 * One path for three ways of arriving here -- the creator ending the session, a participant
 * bowing out, and a device told to wrap up because the creator ended it -- because the work
 * is identical and only the last call differs. Which call that is matters: the creator's
 * `finalise` closes the session for everybody, anyone else's `leave` says only "no more
 * claims from me". Sending the wrong one would let any guest end the conversation.
 *
 * Written to be **pressed twice**, because that is what people do when the first press
 * reports a failure. The first press tears the recorder down; a naive second press then read
 * `recorder.seconds` off null and reported "Could not finish: null is not an object",
 * burying whatever the real first error was. Each stage is guarded by what it needs, and a
 * second press with the session already ended does the only useful thing left: resume
 * polling.
 */
export async function finishSession(note: string | null): Promise<void> {
  if (!engine.session || !engine.stopping.tryEnter()) return;
  const generation = engine.recordingGeneration;
  telemetry.log("stop");
  // Mirrored into the store beside the latch, and released with it below. The microphone
  // closes part-way through what follows, so `recording` stops being the busy window here
  // and this flag becomes it: the update flow and the navigation blocker both read it, and
  // the Stop button is disabled by it rather than by the phase.
  patch({ phase: "wrapping", stopping: true, processingStage: null });
  if (engine.timer) clearInterval(engine.timer);
  engine.timer = null;
  // Aborted, as before: after Stop the result poll below is this device's channel,
  // and its 202 carries the session state and the processing stage.
  if (engine.eventAbort) {
    engine.eventAbort.abort();
    engine.eventAbort = null;
  }
  wrapShow();
  wrapStep("close");
  // Nothing left to join, and no reason to keep emitting into everyone's last chunk while
  // it is being scored.
  stopChirping();

  let lastFlushError: Error | null = null;
  // Set only on a device that reported while the session was still open, and handed to
  // `pollResult` so the wait it opened is the wait that gets closed and logged (#286).
  // `null` means this device is not waiting for anybody, which is every creator and
  // every guest who stopped last.
  let waitingForOthers: { since: number; names: string[] } | null = null;

  // Tell everyone else *first*, before this device spends time on its own ending.
  //
  // [M] The work below took 24 s on a real session -- a 33.5 s final chunk through ECAPA,
  // then a 72 s WAV over the tunnel -- and `finaliseSession` at the end was the only thing
  // that moved the session to `closing`. So two other devices carried on recording for 24.2 s
  // and 22.6 s after their owner had stopped, and everything they captured in that window
  // belonged to nobody.
  //
  // Deliberately not `finaliseSession`: that also marks this membership as finished, which
  // would let the last guest's `leave` start the merge while this device's own claims and
  // audio were still uploading. `close` moves the state and nothing else.
  //
  // Started, not awaited (#272 L1). The announcement is what has to go out
  // early, and it does -- the request is in flight from here. Waiting for its
  // *response* bought nothing and cost 337 ms of this device's stop path on
  // ses_8333. It is awaited below, immediately before `wrapStep("report")`,
  // which is the first step whose correctness depends on the session having
  // moved.
  let closeAck: Promise<unknown> | null = null;
  if (engine.isCreator && !engine.reported) {
    const t0 = performance.now();
    closeAck = api
      .closeSession(engine.session.session_id)
      .then((value: unknown) => {
        telemetry.log("close", {}, performance.now() - t0);
        return value;
      })
      .catch(() => {
        telemetry.log("close", { ok: false }, performance.now() - t0);
        /* The 3 s state poll on the other devices is a backstop for this. */
        return undefined;
      });
  }

  try {
    if (engine.recorder) {
      say(note || t("session.saving_part_short"));
      const elapsed = engine.recorder.seconds;

      // Let go of the microphone first. Everything below reads the buffer, not the device.
      //
      // [M] It used to be the last step, which meant a phone kept its microphone open --
      // recording indicator lit, red dot in the status bar -- for the whole of its own
      // scoring pass and audio upload. On a real session that was ~25 s after the owner had
      // pressed stop, and it reads as the app ignoring you. Nothing about that audio was
      // ever going to be used.
      // The live view ends with the recording, and it ends *before* the recorder does.
      // The stream reads this recorder's buffer, so stopping in the other order would
      // leave a socket flushing frames out of a capture the session has finished with.
      //
      // The post-ready grace closes here too, and here is the moment it must: the forced
      // `ensureDeltaMode` below is what turns a producer that lost the race to Stop into a
      // reported verdict rather than a silent recording without claims, and it must answer
      // rather than wait (#170-D).
      closeContextGraceForStop();
      await stopStreamForRecordingEnd();

      const recorder = engine.recorder;
      await recorder.stop();
      patch({ recording: false, capturePause: null });
      await releaseWakeLock();

      // Wait for a scoring pass that is already running before starting the final one.
      //
      // [M] `chunkCursor` only advances when a pass *completes*, so a stop press landing
      // mid-pass left it at the previous boundary -- and the final flush then re-scored a
      // span the device had just finished. On a real session that made the tail 36.5 s of
      // audio instead of 14 s, 22 of them scored twice, which ate the whole saving from the
      // coarser tail hop. The interval is already cleared above, so nothing new can start;
      // this only waits out what was in flight, and it is bounded because a pass that never
      // finishes must not strand somebody on a spinner.
      if (scoring) {
        wrapStep("score");
        say(t("session.saving_part_short"));
        const deadline = Date.now() + 45000;
        while (scoring && Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 100));
        }
        if (scoring) console.warn("final flush proceeding while a pass is still running");
        say(note || t("session.saving_part_short"));
      }

      {
        try {
          // Settle the payload protocol *before* the final pass, never as a side effect of
          // it (#170-C).
          //
      // `stopStreamForRecordingEnd` above has already cancelled the readiness latch
      // with `recording_stopped`, so this resolves at once and does not put a
      // readiness deadline into a wrap-up somebody is watching.
          await ensureDeltaMode();
          // Unconditional now, and cheap. It used to be gated on there being more than half a
          // second of unscored audio, because the work behind it was a whole chunk; what is
          // left here is one window at most, plus a harvest of windows already scored during
          // the conversation. Skipping it on that old test would now drop finished work.
          wrapStep("score");
          // Gated in delta mode for the reason the tick is: `advance()` is what fills
          // the scorer, and scoring an entire stalled session here -- for a writer that
          // is full or finished -- is the unbounded growth this policy exists to stop.
          if (engine.deltaMode && deltaScoringAllowed()) {
            await engine.scorer?.advance(readFrom(recorder), elapsed);
          }
          await scoreAndQueueChunk(engine.chunkCursor, elapsed, { final: true });
        } catch (err) {
          // Best effort, but recorded rather than discarded. The report still
          // needs to reach the server when a final scoring pass fails.
          lastFlushError = err as Error;
        }
      }

      // The delta writer drains on the same seam and for the same reason: `finaliseSession`
      // below starts the merge, and an unacknowledged delta after that point is speech
      // nobody is credited for. The carry is pushed in first so a split tail is not left
      // behind the queue it never entered.
      if (engine.deltaMode) {
        // Production is finished at this point: the recorder is stopped, the in-flight
        // scoring pass has been awaited, and the final pass has run. Nothing can create
        // another claim draft, which is what makes the drain below able to *end*.
        //
        // Pump *between* drains, not once before them. The carry only exists because the
        // queue was full; the drain is exactly the window in which it empties, so a
        // single pump would report split tail pieces as unsent without ever having tried
        // them again.
        let drained = false;
        const deadline = Date.now() + DELTA_DRAIN_MS;
        do {
          pumpClaimDeltas();
          drained =
            (await (engine.deltaOutbox?.drain(DELTA_DRAIN_MS) ?? Promise.resolve(true))) &&
            engine.deltaCarry.length === 0;
        } while (!drained && engine.deltaCarry.length > 0 && Date.now() < deadline);
        const unsent = deltaWaiting() + engine.deltaAbandoned;
        if (!drained || engine.deltaAbandoned) {
          console.warn(`finishing with ${unsent} claim delta(s) unsent`);
          telemetry.log("claim_delta_unsent", {
            waiting: deltaWaiting(),
            abandoned: engine.deltaAbandoned,
            halted: engine.deltaHalted,
          });
          say(
            t("session.words_unnamed"),
            "warn"
          );
        }
      }

      // The microphone and the wake lock were released above. All that is left is to drop
      // the buffer.
      engine.recorder = null;
      // And this session's cached window scores with it -- they describe a recording that no
      // longer exists, and the next session mints its own scorer against its own template.
      engine.scorer = null;
    }

    // Audio this device still owes goes before its marker and its report (#894). Once
    // this device has left -- or once every report is in -- the server refuses it a stream
    // ticket, and once every marker is in the closing sweep can move the meeting on, so a
    // backlog still owed after them is lost. An online stop owes nothing past the last
    // frame's ack and barely waits; a device that was offline at Stop waits for the upload
    // that `stopStreamForRecordingEnd` started, bounded by its window from Stop. The apps
    // hold the same two calls the same way (#845).
    if (!engine.reported) await awaitAudioDelivered();

    // The marker, between the audio upload and the report (#170-B).
    //
    // Ordering, and the order is the whole contract: every delta has been acknowledged
    // above, so the pair sealed here is exactly what the ledger holds; the report below
    // is what tells the server this device is finished, and the server refuses it until
    // this row exists. Audio deliberately sits in front of it and is deliberately allowed
    // to fail -- audio buys a transcript, the marker buys attribution, and one is never a
    // substitute for the other.
    if (engine.deltaMode && engine.deltaGate && !engine.reported) {
      wrapStep("watermark");
      const gate = engine.deltaGate;
      try {
        // Derived from durable state, never from a local (#170-B) [V].
        //
        // This used to read a `let` scoped to one `finishSession` call. A marker that
        // committed and a `leave` that then answered `409 final_watermark_pending` -- the
        // ordinary case while *another* device still owes its marker -- left the wrap-up
        // through the catch, and the next Stop press started with that local `false` and
        // threw `drain_incomplete` before the already-durable marker could reach the
        // report again. The session could never finish, because the one thing it needed
        // to retry was gated on a fact it had thrown away.
        //
        // `gate.isCommitted` and the outbox both live on `engine` and survive teardown,
        // so a second press resumes from what is true rather than from what this call
        // happens to remember.
        if (!gate.isCommitted) {
          if (!deltaWriterSettled()) {
            // Named, not silent, and *not* a reason to report anyway. There is no
            // fallback here: nothing but the delta writer can discharge a delta epoch the
            // server is already counting.
            throw new MarkerTerminalError(
              "drain_incomplete",
              `${deltaWaiting() + engine.deltaAbandoned} claim delta(s) were never ` +
                "acknowledged, so this device cannot say it has sent them all"
            );
          }
          say(t("session.finishing_part"));
          gate.sealAck(engine.deltaOutbox?.finalAck() ?? null);
        }
        // Idempotent by construction: a committed marker returns its outcome without a
        // second request, so a report retry never publishes twice.
        const outcome = await gate.publishOnce();
        telemetry.log("claim_delta_watermark_committed", {
          outcome,
          sequence: gate.sealedAck?.sequence ?? -1,
        });
      } catch (raw) {
        const reason =
          raw instanceof MarkerTerminalError ? raw.reason : "marker_refused";
        engine.deltaHalted = true;
        telemetry.log("claim_delta_watermark_failed", { reason });
        say(
          t("session.watermark_failed"),
          "err"
        );
        // Deliberately *before* the report. Leaving without a marker would tell the
        // server this device had finished when it had not, and the whole point of the
        // barrier is that no merge starts on that claim.
        //
        // A plain `return`: the outer `finally` releases the stop latch, clears
        // `stopping` and tears down, so the buttons come back and the failure stands on
        // screen. `engine.reported` stays false, which is what keeps this device honest
        // about never having said it was done.
        return;
      }
    }

    if (!engine.reported) {
      // The announcement has to have landed before this device reports itself
      // done: `finalise` is what starts the merge, and the state move `close`
      // makes is what tells the other devices to wrap up first. Awaiting it here
      // rather than at the call site is the whole of L1's saving on the
      // creator's chain -- the request has been in flight through the recorder
      // stop, the final scoring pass and the watermark POST, so by now it has
      // almost always already answered.
      if (closeAck) await closeAck;
      wrapStep("report");
      say(engine.isCreator ? t("session.ending_for_everyone") : t("session.telling_others"));
      const t0 = performance.now();
      const report: () => Promise<FinaliseResponse> = engine.isCreator
        ? () => api.finaliseSession(engine.session!.session_id)
        : () => api.leaveSession(engine.session!.session_id);
      if (!engine.finaliseRetry) {
        engine.finaliseRetry = new FinaliseRetryController(report, {
          timer: finaliseRetryTimerForTest ?? FINALISE_RETRY_TIMER,
          onRetrying: (attempt: number) => {
            say(FINALISE_RETRYING_MESSAGE);
            // One row per 409 this device actually saw (#272 L2). The server
            // records its own refusals; the gate checks both, because a
            // client-only count misses a refusal whose response never arrived
            // and a server-only count misses a retry driven by a timeout.
            telemetry.log("finalise_retry", { attempt, status: 409 });
          },
        });
      }
      const out = await engine.finaliseRetry.run();
      telemetry.log(engine.isCreator ? "finalise" : "leave", {}, performance.now() - t0);
      engine.reported = true;
      patch({ reported: true });
      // Whether this device is now waiting for other people, decided from the report's
      // own response rather than from a poll: `leave` answers a `SessionOut`, so the
      // state is already in hand and the wrap card can be right before the first 202
      // ever arrives (#286).
      //
      // Never for the creator. The creator's `close` precedes this report, so a creator
      // reading `recording` here would be reading a response that raced its own
      // announcement -- and the creator is not waiting for anybody by definition.
      if (!engine.isCreator && out?.state && OPEN_STATES.has(out.state)) {
        waitingForOthers = { since: waitClock().now(), names: out.pending ?? [] };
      } else if (out?.pending?.length) {
        say(t("session.waiting_others_finish"));
      }
    }
    if (engine.claimsRefused > 0) {
      // Reported here as well as when it happened: the status line has been overwritten
      // several times since, and a refusal is a permanent hole in this device's claim
      // rather than something the wrap-up made good.
      say(
        t("session.words_unnamed"),
        "warn"
      );
    }
    if (lastFlushError) {
      // The session still finalises and the audio still uploaded, but this device's last
      // stretch never became a claim. That is a real loss and it gets said.
      say(
        t("session.last_words_unnamed"),
        "warn"
      );
    }
    // The busy window ends with the report, not with the summary.
    //
    // [V] Released here rather than in the `finally`, because `stopping` arms the navigation
    // blocker in `routes/session.tsx` -- `useBlocker(() => recording || stopping)` -- and
    // `pollResult` below is what sets `phase: "done"`, which is what that screen's effect
    // navigates on. Holding the flag across the poll meant the engine asked for the result
    // screen and the blocker refused its own app's navigation, reset it, and printed "Still
    // recording on this device" over a session that had already finalised. Nothing retried:
    // the effect's deps are `[phase, resultSessionId]` and neither changes again once the
    // result has landed, so clearing the flag afterwards came too late for good. Pressing
    // Stop again re-ran all of this and was refused the same way -- [M] three `stop` events
    // and three `result_ready` events on one device, ending on the recording screen with a
    // complete transcript and summary sitting on the server.
    //
    // Nothing is lost by leaving now, which is why this is the right place and not merely
    // the convenient one: the tail chunk is uploaded, the outbox is drained and the report
    // has landed. `pollResult` is deliberately written to outlive this screen -- see its own
    // note -- so navigating away during it is the case it was built for, not a hazard.
    patch({ stopping: false });
    // The gateway's close, collected here (#272 L1). This is the wait that used
    // to sit in front of final scoring and the watermark POST and cost 2.4-3.0 s
    // of the stop path; the socket has been closing throughout everything above,
    // so by now it has usually already settled. It is still awaited, so the last of
    // this device's audio is on the server before the screen says it is.
    await awaitStreamClosed();
    // The step this device is actually on. `waiting` for a guest whose creator is still
    // recording; everybody else's device is finished, and the server has the meeting.
    if (waitingForOthers) {
      wrapStep("waiting");
      sayWaitingFor(waitingForOthers.names);
    } else {
      enterProcessing();
    }
    await pollResult(waitingForOthers);
  } catch (err) {
    if (err instanceof FinaliseRetryExhaustedError) {
      // The retry controller has proved this is the typed pending-watermark case. Keep the
      // terminal copy stable and actionable; all unrelated errors retain the old wording.
      say(FINALISE_RETRY_EXHAUSTED_MESSAGE, "err");
    } else if (err instanceof FinaliseRetryAbortedError) {
      // Abort/unmount is not a user-visible report failure and must not be replaced by a
      // misleading generic "Could not finish" message.
    } else {
      // The detail goes to the console, not the screen: the person can act on "try again",
      // not on an error message.
      console.warn("finishSession failed", err);
      say(
        engine.reported
          ? t("session.check_notes_failed")
          : t("session.leave_failed"),
        "err"
      );
    }
  } finally {
    // Processing can take minutes and the person may have left and started another
    // recording in the meantime. That recording owns the latch, the recorder and the
    // screen now, and this wrap-up's cleanup must not touch any of them.
    if (engine.recordingGeneration === generation) {
      engine.stopping.release();
      // In the same breath as the latch, so the two cannot drift. [V] Nothing used to undo
      // `phase: "wrapping"`, so a wrap-up that threw -- or a `pollResult` that ran out of
      // attempts -- left Stop disabled for good, under an error that said "Press again to
      // keep waiting for the summary" beside a button that could not be pressed. The only
      // live control left was Discard, on a session whose audio was already uploaded.
      patch({ stopping: false });
      await teardown();
      // Either the result screen is up, or an error is on screen and the buttons are needed
      // again. Leaving a stalled progress bar behind would be worse than no bar.
      wrapHide();
    }
  }
}

/**
 * This device is finished; the server has the meeting (#358).
 *
 * The wrap-up card gives way to "Processing. We'll email you when it's ready." Nothing
 * on the device is at risk from here, so leaving is safe: the poll that follows only
 * decides whether *this* screen gets to show the result, and the email covers the rest.
 */
function enterProcessing(): void {
  if (useApp.getState().phase === "processing") return;
  wrapHide();
  patch({ phase: "processing" });
}


/**
 * Poll until the result exists, then keep polling until the summary joins it.
 *
 * The server publishes the attributed transcript the moment it is done and writes the
 * summary in a second pass, because [M] summarising is the one stage that cannot start until
 * the whole transcript exists. So "ready" means *readable*, not finished: publish it
 * immediately, then carry on for the summary rather than making someone stare at a spinner
 * while the words they said are already sitting on the server.
 *
 * The polling stays here rather than moving to the result screen: it started before that
 * screen existed and it must survive somebody navigating away from it.
 *
 * The attempt cap is expressed as wall-clock patience rather than a bare count, so tightening
 * the early interval cannot quietly shorten how long this waits. It used to be a flat 200
 * attempts at 1500 ms; spending part of that budget on a fast early window would otherwise
 * have cut the total from ~300 s to ~260 s, which is a behaviour change nobody asked for.
 */
async function pollResult(
  waitingForOthers: { since: number; names: string[] } | null = null
): Promise<void> {
  if (!engine.session) return;
  const sessionId = engine.session.session_id;
  // Processing can outlast this screen, and a new recording started meanwhile owns the
  // phase and the result slot. This poll answers only for the recording it began in.
  const generation = engine.recordingGeneration;
  const superseded = (): boolean => engine.recordingGeneration !== generation;
  const t0 = performance.now();
  // The result budget's own origin, rebased when a wait for other people ends. Two
  // clocks rather than one on purpose: `t0` is what `result_ready` and `summary_ready`
  // are measured from (the stop path, which is what #272's 5 s target counts), while
  // this one decides the poll interval and the attempt budget. Sharing them would mean
  // a guest who waited four minutes for the creator spent its whole 15 s fast window
  // on that wait and then polled the actual merge at 1500 ms -- the slowest possible
  // cadence at the one moment something is about to arrive.
  let budgetFrom = t0;
  let rendered = false;
  // One event per stage, not one per poll. `result_ready` marks the transcript
  // becoming readable; `summary_ready` marks the summary joining it. Logging on
  // every poll while `summary_pending` is true produced a hundred-plus
  // `result_ready` lines per session that drowned the real timeline.
  let resultLogged = false;
  /** One `wrap_waiting` row per device per session, when the wait ends (#286, Q2). */
  let waitLogged = false;
  const leaveWaiting = (outcome: "ended" | "timed_out"): void => {
    if (!waitingForOthers || waitLogged) return;
    waitLogged = true;
    // On the wait's own clock, which is the clock `since` was taken from. Mixing the
    // two would put a stop-path duration on a row the gate compares against the gap
    // between two devices' stops.
    telemetry.log("wrap_waiting", { outcome }, waitClock().now() - waitingForOthers.since);
    waitingForOthers = null;
    // The merge's budget starts now, not at Stop.
    budgetFrom = performance.now();
  };
  for (let attempt = 0; attempt < RESULT_POLL_ATTEMPTS; ) {
    const out = await api.getResult(sessionId);
    if (superseded()) return;
    if (out.ready) {
      // Straight from `recording` to a published result is possible -- the creator
      // stopped between two polls -- so the wait is closed here as well as below.
      leaveWaiting("ended");
      if (!resultLogged) {
        telemetry.log("result_ready", {}, performance.now() - t0);
        resultLogged = true;
      }
      patch({ result: out.result, resultSessionId: sessionId, phase: "done", processingStage: null });
      rendered = true;
      if (!summaryPendingOf(out.result)) {
        telemetry.log("summary_ready", {}, performance.now() - t0);
        return;
      }
    } else if (out.degraded || pendingSessionState(out) === "failed") {
      // Terminal: processing failed. The result screen says so plainly and offers the
      // creator a retry, so this device goes there rather than holding a status line.
      leaveWaiting("ended");
      patch({ result: null, resultSessionId: sessionId, phase: "done", processingStage: null });
      return;
    } else {
      // The 202, read for state rather than for prose (#286). `null` is an older
      // server and resolves to the processing path.
      const state = pendingSessionState(out);
      patch({ processingStage: processingStageOf(out.processing_stage) });
      // Never the creator, here as well as at the report (S2.c). A creator polling a
      // session that still reads `recording` is looking at its own `close` not having
      // landed -- which is a thing to retry, not a thing to wait for somebody else to
      // do, and "waiting for the others to end the session" would be false on the one
      // device that ends it.
      if (!engine.isCreator && state !== null && OPEN_STATES.has(state)) {
        if (!waitingForOthers) {
          // A guest that reported while the session was already `closing` and then saw
          // it reopen cannot happen; this is the device that stopped before its own
          // report response could say so, plus the creator-less edge where the report
          // raced. Either way the card is honest from here on.
          waitingForOthers = { since: waitClock().now(), names: pendingNames(out) };
        } else {
          waitingForOthers = { since: waitingForOthers.since, names: pendingNames(out) };
        }
        wrapStep("waiting");
        sayWaitingFor(waitingForOthers.names);
        // Whichever bound arrives first (Q3). `expires_at` absent leaves the 20 minutes
        // standing, because absent is unknown and not "expired" -- the accessor returns
        // `null` rather than a zero for exactly this comparison.
        const clock = waitClock();
        const waited = clock.now() - waitingForOthers.since;
        const expiresAt = pendingExpiresAtMs(out);
        const expired = expiresAt !== null && clock.epochMs() >= expiresAt;
        if (waited >= WAIT_FOR_END_MAX_MS || expired) {
          const names = waitingForOthers.names;
          leaveWaiting("timed_out");
          // Two facts, two sentences, and deliberately *not* "This is taking longer
          // than expected" (#151-B D5): nothing is taking longer than expected. The
          // session is still running, and the reader's next move is to wait for a
          // person rather than to reopen the app.
          say(
            t("session.transcript_after", { who: sessionStillRecording(names) }),
            "warn"
          );
          return;
        }
        // Deliberately outside the attempt counter: this poll spent no result budget,
        // because there is no result to wait for yet. There is also nothing fast to
        // catch, so it is always the settled interval.
        await clock.sleep(RESULT_POLL_MS);
        continue;
      }
      // `closing`, `finalising`, or an older server's silence: processing is the wait
      // from here, and today's budget starts from this moment.
      leaveWaiting("ended");
      enterProcessing();
    }
    attempt += 1;
    await new Promise((r) => setTimeout(r, performance.now() - budgetFrom < RESULT_POLL_FAST_MS ? RESULT_POLL_EARLY_MS : RESULT_POLL_MS));
    if (superseded()) return;
  }
  say(
    rendered
      ? t("session.summary_pending_notice")
      : t("session.still_processing"),
    "warn"
  );
}

// ---------------------------------------------------------------------
// discard
// ---------------------------------------------------------------------

/**
 * "I changed my mind." Audio already sent is deleted; this device leaves the session.
 *
 * **A guest's action only.** The creator has no part to discard, because only the
 * creator's `finalise` moves a session toward the merge: a host who discarded and left
 * left an open session nobody could close. The screen hides the button for the host and
 * `finishSession` is their way out; this guard is the engine's backstop (the server
 * refuses it too).
 */
export async function discardSession(): Promise<string | null> {
  if (!engine.session || engine.isCreator || engine.stopping.busy) return null;
  const ok = window.confirm(t("session.discard_confirm"));
  if (!ok) return null;

  if (!engine.stopping.tryEnter()) return null;
  const sessionId = engine.session.session_id;
  try {
    say(t("session.deleting_part"));
    // Microphone first: nothing new should be captured, let alone uploaded, behind a delete.
    // Any chunk already in flight is covered by `discardAudio` below, which deletes by
    // session and device rather than by a list gathered beforehand.
    await teardown();

    // Delete before leaving. If the creator has already stopped, leaving could be the last
    // report the merge is waiting for -- and it must not read audio on its way out.
    const out = await api.discardAudio(sessionId);
    if (!engine.reported) {
      await api.leaveSession(sessionId).catch(() => {
        /* Best-effort: the audio is already gone, which is what was asked. */
      });
      engine.reported = true;
      patch({ reported: true });
    }
    engine.session = null;
    patch({ phase: "left", session: null, processingStage: null });
    return out.files_deleted
      ? t("session.deleted_and_left")
      : t("session.left_nothing_saved");
  } catch (err) {
    console.warn("discardSession failed", err);
    say(t("session.delete_failed"), "err");
    return null;
  } finally {
    engine.stopping.release();
  }
}

// ---------------------------------------------------------------------
// the chirp beacon
// ---------------------------------------------------------------------

/**
 * An AudioContext created while a click is still fresh.
 *
 * Safari only permits a page to start audio playback while it holds user activation, and
 * activation does not survive an arbitrary `await`. Creating a session involves a network
 * round trip, so the context for the automatic chirp has to be constructed synchronously
 * inside the click handler and used afterwards -- built after the round trip, it is refused.
 */
export function newChirpContext(): AudioContext | null {
  try {
    const ctx = new AudioContext();
    ctx.resume().catch(() => {});
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Broadcast the join code until switched off.
 *
 * Repeated bursts rather than a continuous tone. A receiver only has to catch one frame, and
 * the reasons a permanent 18 kHz emission is a poor neighbour to a live microphone all scale
 * with duty cycle: gain control reacts to energy it cannot hear, iOS can flip the audio
 * session into an echo-cancelling mode when playback and capture run together, and 18 kHz is
 * inaudible to most adults but not to children or dogs.
 *
 * One AudioContext for the whole run, reused, so this is not renegotiating the device's audio
 * route every few seconds while everyone is being recorded.
 */
async function chirpLoop(): Promise<void> {
  // Reuse the context the click handler built, if there is one: on Safari a context
  // constructed after an await has already lost its permission to make a sound. Held in a
  // local, not read back from module state, so a run that is still winding down cannot close
  // the context belonging to a run that has just begun.
  const ctx = engine.chirpCtx ?? newChirpContext();
  engine.chirpCtx = ctx;
  if (!ctx || !engine.session) {
    engine.chirping = false;
    patch({ chirping: false });
    say(t("session.chirp_unavailable"), "warn");
    return;
  }

  // Measured from the moment this run starts broadcasting, not from session creation -- so a
  // manual re-toggle gets a fresh dense window, which is right: somebody pressing the button
  // is somebody trying to get a person in right now.
  const startedAt = performance.now();
  const code = engine.session.join_code as string;

  while (engine.chirping) {
    try {
      await chirp.transmit(code, { ctx });
    } catch {
      engine.chirping = false;
      patch({ chirping: false });
      say(t("session.chirp_unavailable"), "warn");
      break;
    }
    // Dense while people are joining, sparse afterwards.
    const elapsed = (performance.now() - startedAt) / 1000;
    const gap = elapsed < K.CHIRP_DENSE_SEC ? K.CHIRP_DENSE_REPEAT_SEC : K.CHIRP_REPEAT_SEC;
    for (let i = 0; i < gap * 10 && engine.chirping; i++) {
      await new Promise((r) => setTimeout(r, 100));
    }
  }

  try {
    await ctx.close();
  } catch {
    /* Already closed. */
  }
  if (engine.chirpCtx === ctx) engine.chirpCtx = null;
}

export function stopChirping(): void {
  engine.chirping = false;
  patch({ chirping: false });
}

export function startChirping(ctx: AudioContext | null = null): void {
  if (engine.chirping) return;
  if (ctx) engine.chirpCtx = ctx;
  engine.chirping = true;
  patch({ chirping: true });
  // Worth saying plainly: the door is open for as long as this is on, and joining a session
  // means receiving its full transcript afterwards.
  say(
    t("session.chirp_on"),
    "warn"
  );
  void chirpLoop();
}

/**
 * The button. Repaints immediately rather than waiting for the loop to notice.
 *
 * [A] The loop only reaches its own reset after the in-flight `chirp.transmit` resolves --
 * 0.15 s + 14 tones x 0.06 s for a five-character code, so ~1 s -- and the status line
 * updates at once. The result was a second of the screen contradicting itself: "Stopped
 * broadcasting the code." underneath a button still reading "Broadcasting — tap to stop".
 */
export function toggleChirp(): void {
  if (engine.chirping) {
    stopChirping();
    say(t("session.chirp_off"));
    return;
  }
  // A tap is its own activation, so no pre-built context is needed here.
  startChirping();
}

// ---------------------------------------------------------------------
// create and join
// ---------------------------------------------------------------------

/** Refuse a start under an unresolved device link. See `recordingIdentityBlock` (#38). */
function assertRecordingIdentity(): void {
  if (recordingIdentityBlock(useApp.getState()) !== null) {
    throw new Error(t("session.identity_block"));
  }
}

/**
 * Refuse a start on a deployment that takes no audio (#388). Checked before the session
 * is created or joined, so no microphone opens and no half-made session is left behind;
 * the server would refuse the uplink anyway. See `recordingPaused`.
 */
function assertRecordingAllowed(): void {
  if (recordingPaused(useApp.getState())) throw new Error(RECORDING_PAUSED_NOTICE);
}

/**
 * Start a meeting. The voice model must already be ready (`ensureModelReady`, #658): waiting
 * for it *before* the session exists is what lets a Cancel mid-load leave nothing behind.
 * The meeting starts unnamed (#708): its summary or its creator names it later.
 */
export async function createSession(
  chirpCtx: AudioContext | null,
  setup: SetupOptions = {}
): Promise<void> {
  // Engine backstop, not the only gate: Home re-checks on every path that reaches it, but
  // a caller outside Home (a deep link, a future screen) must not be able to start a
  // microphone under an account whose device link is unresolved (#38).
  assertRecordingIdentity();
  assertRecordingAllowed();
  await assertMicNotBlocked();
  const signal = beginSetup(setup.signal);
  if (signal.aborted) throw setupAborted();
  captureAnalytics("session_create_started", {});
  const t0 = performance.now();
  let session: Awaited<ReturnType<typeof api.createSession>>;
  try {
    session = await api.createSession();
  } catch (err) {
    engine.setupAbort = null;
    captureAnalytics("session_create_failed", { category: "request" });
    throw err;
  }
  telemetry.log("create", {}, performance.now() - t0);
  // `startRecording` either records or throws -- it returned an unconditional `true`, and
  // the branch that read it was waiting for a value it could not produce. The throw is not
  // unhandled: the caller closes the chirp context in its own `catch` (`routes/home.tsx`),
  // which is where the context was opened and so where it belongs.
  try {
    await startRecording(session, { ...setup, signal, tappedAt: setup.tappedAt ?? t0 });
  } catch (err) {
    // The session exists and nothing was recorded into it: leave it, and close the
    // microphone, whether the start failed or was cancelled (#658). Only this await is
    // covered: past it the meeting is live, and nothing after may undo it.
    await abandonStart(session.session_id, { creator: true });
    if (!isSetupAbort(err)) captureAnalytics("session_create_failed", { category: "recording" });
    throw err;
  }
  startChirping(chirpCtx);
  captureAnalytics("session_created", {});
}

/** Join a meeting. As `createSession`, the voice model is made ready first (#658). */
export async function joinSession(code: string, method: string, setup: SetupOptions = {}): Promise<void> {
  // The same backstop as `createSession`, and the same reason: a join reaches the
  // microphone and, once it does, the collapse is already committed (#38).
  assertRecordingIdentity();
  assertRecordingAllowed();
  const credentials = useApp.getState().credentials;
  if (!credentials) throw new Error(t("session.not_set_up"));
  await assertMicNotBlocked();
  const signal = beginSetup(setup.signal);
  if (signal.aborted) throw setupAborted();
  const analyticsMethod = method === "chirp" ? "chirp" : "code";
  const t0 = performance.now();
  let session: Awaited<ReturnType<typeof api.joinSession>>;
  try {
    session = await api.joinSession(code, credentials.claim_profile_id, method);
  } catch (err) {
    engine.setupAbort = null;
    captureAnalytics("session_join_failed", { join_method: analyticsMethod, category: "request" });
    throw err;
  }
  telemetry.log("join", { join_method: method }, performance.now() - t0);
  try {
    await startRecording(session, { ...setup, signal, tappedAt: setup.tappedAt ?? t0 });
  } catch (err) {
    // A guest joined a real meeting, so it only leaves (#663).
    await abandonStart(session.session_id, { creator: false });
    if (!isSetupAbort(err)) {
      captureAnalytics("session_join_failed", { join_method: analyticsMethod, category: "recording" });
    }
    throw err;
  }
  captureAnalytics("session_joined", { join_method: analyticsMethod });
}

/** Whether a failed start was a Cancel, which no screen reports as an error. */
export { isSetupAbort };

/** For the phase-driven route: the engine is done with this screen. */
export function currentPhase(): SessionPhase {
  return useApp.getState().phase;
}
